/**
 * Trade mode on Portfolios → View portfolio (TRADE-1 step 3b-1, Pedro 2026-10-22/23; a button
 * there since TRADES-RM, for whoever holds `trades.draft`, migration 0118; design of record
 * `_MEMORY/decisions/2026-10-23_trades-trade-mode-design.md`). It sits on View portfolio's grid
 * (PortfolioViewTab): a Trade button; the unit (Quantity / Value (Ref Ccy) / % of portfolio;
 * % to start, no dialog — Pedro 2026-10-23 — changed with the trade bar's buttons, entries
 * converted); columns right of the portfolio's own: Buy,
 * Sell, the amount, one column per cash account to settle each trade (pre-filled from a
 * standard choice: reference currency / security currency / other), the reason for keeping a
 * not-approved holding; then the after-view: Traded, Traded value, New Quantity, New Value,
 * New %; Buy / Sell / Net total rows under the Total. Drafts are saved, reopened, edited and
 * deleted (`api/trades`); a draft changes nothing in the portfolio.
 * 3b-1 covers the securities the portfolio holds; buying a new security (3b-2) and drafts
 * built on other drafts (3b-3) come next.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon, Alert, Button, Checkbox, Group, Menu, Modal, Radio, SegmentedControl, Select, Stack, Table, Text,
  TextInput, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconArrowsExchange, IconInfoCircle, IconTrash } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { portfoliosApi, type ConsolidatedRow, type ConsolidatedView } from '../api/portfolios';
import { modelsApi } from '../api/models';
import { agreementPortfoliosApi } from '../api/agreementPortfolios';
import type { Consolidation } from '../api/portfolioConsolidations';
import { ConsolidationPicker } from '../components/ConsolidationPicker';
import { securitiesApi, type SecurityRow } from '../api/securities';
import {
  tradesApi, type PriceSource, type SettleDefault, type TradeDocLine, type TradeDocOrder, type TradeDraft,
  type TradeDocIn, type TradeDraftIn, type TradeLineIn,
} from '../api/trades';
import { ColumnResizeHandle } from '../components/ColumnResizeHandle';
import { PortfolioPicker } from '../components/PortfolioPicker';
import { useDirtyForm } from '../components/DirtyFormProvider';
import { AdminOverrideSwitch, useAdminOverride } from '../hooks/useAdminOverride';
import { ordered, useColumnOrder, useColumnWidths, useHeadingDrag } from '../components/gridColumnOrder';
import { convert, grossIn, measure, ratesFor, type Ctx } from '../utils/portfolioConsolidation';
import {
  adjust, fromQuantity, planCashTargets, suggestTransfers, sweepExcess, targetSellsAll, targetTrade, toQuantity, type Side, type Unit,
} from '../utils/tradeMath';
import { ModelPicker } from '../components/ModelPicker';
import { CONSOLIDATED_ACCESS, flattenGroups, groupRows, type GroupLens, type GroupLevel } from '../utils/portfolioGrouping';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');
const fmt = (v: number | null, digits: number, max = digits) =>
  v == null ? '-' : v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: max });
const amount = (v: number | null) => fmt(v, 2);
const qtyFmt = (v: number | null) => fmt(v, 0, 6);
const pctFmt = (v: number | null) => (v == null ? '-' : `${fmt(v, 2)}%`);
const n = (s: string | null | undefined) => (s == null || s === '' ? null : Number(s));
/** A float as sent to the server: 12 significant digits, so 0.1 + 0.2 does not travel as
 *  0.30000000000000004 and fail the lot check. */
const clean = (v: number) => String(Number(v.toPrecision(12)));


/** One row's trade entry (by row key). */
interface Entry { side: Side | null; amount: string; settle: string | null; note: string }
const EMPTY: Entry = { side: null, amount: '', settle: null, note: '' };
/** TP-XFER (Pedro 2026-10-24, migration 0119): cash moved between two of the portfolio's cash
 *  accounts. TF-XFER2 (Pedro 2026-10-25): `out` leaves `from` in its currency, `inn` reaches `to`
 *  in its currency (pre-filled at the custodian's rate, editable: an FX trade). */
interface Transfer { from: string; to: string; out: number; inn: number }
/** A transfer being edited in the window (Out as typed; In worked out from it, read-only). */
interface XferRow { from: string | null; to: string | null; out: string; inn: string }

/** A trade column (rendered after the portfolio's own columns). */
interface TCol { key: string; label: string; tip: string; width: number; num?: boolean; after?: boolean }

/** A security added to buy (not held): its master data and the price it trades at. */
export interface Added {
  sec: SecurityRow;
  price: string;                 // in the security's currency, as quoted
  source: PriceSource | null;    // TQ3 / TQ3a rung; 'typed' once edited; null = none found
  priceDate: string | null;
  factor: number;                // value per unit = price × factor (0.01 = per 100 nominal)
  factorGuessed: boolean;
  accrued: string;               // per unit, in accruedCcy (TQ9: borrowed, or typed)
  accruedCcy: string;
}
export const NEW_PREFIX = 'new:';
export const addedRow = (a: Added): ConsolidatedRow => ({
  key: `${NEW_PREFIX}${a.sec.id}`, is_cash: false, is_loan: false, name: a.sec.description, isin: a.sec.isin,
  currency: a.sec.currency, security_id: a.sec.id, bb_ticker: a.sec.bb_ticker, figi: a.sec.figi,
  authorized: a.sec.authorized, min_piece: a.sec.min_piece == null ? null : String(a.sec.min_piece),
  min_increment: a.sec.min_increment == null ? null : String(a.sec.min_increment),
  classifications: Object.fromEntries(a.sec.classifications.map((c) => [c.dimension_code, c.path])),
  orders: {}, non_classified: false, by_portfolio: {},
});
export const SOURCE_TEXT: Record<string, string> = {
  own: "the portfolio's own line",
  same_custodian_day: 'another portfolio of the same custodian, same day',
  other_custodian_day: "another custodian's portfolio, same day",
  securities: 'the Securities price of that day',
  same_custodian_last: "the same custodian's last price",
  typed: 'typed',
};

const UNIT_LABEL = (u: Unit, ref: string) => (u === 'quantity' ? 'Quantity' : u === 'value' ? `Value (Ref Ccy ${ref})` : '% of portfolio');

export interface TradeMode {
  enabled: boolean; active: boolean;
  /** The Trade / Leave trade mode button (toolbar line, right-aligned). */
  button: React.ReactNode;
  /** The trade toolbar and its messages (above the table). */
  panel: React.ReactNode;
  width: number;
  headCells: (rowSpan: number, top: number) => React.ReactNode[];
  rowCells: (row: ConsolidatedRow, bg: string) => React.ReactNode[];
  blankCells: (bg: string, bold?: boolean, bottom?: number) => React.ReactNode[];
  /** The Buy / Sell / Net total rows (after the Total): label + the trade cells at a sticky offset. */
  footRows: { label: string; tip: string; cells: (bottom: number) => React.ReactNode[] }[];
  /** 3b-2: securities added to buy, as rows to show at the top of the table. */
  addedRows: ConsolidatedRow[];
  /** 3b-2: the "buy a new security" search, for the first cell of the row under the header. */
  searchCell: React.ReactNode;
  /** I1: "Traded only" is on; and which rows carry a trade (shaded). */
  onlyTraded: boolean;
  isTraded: (r: ConsolidatedRow) => boolean;
  rowShade: (r: ConsolidatedRow) => string | null;
  /** TRADE-CONS 4b (S4-Q2): a security row's per-portfolio rows, shown under it when expanded
   *  (consolidated trade mode only): the label for the first column, and the trade cells. */
  subRows?: (r: ConsolidatedRow) => { key: string; label: React.ReactNode; shade: string | null; cells: (bg: string) => React.ReactNode[] }[];
}

export function useTradeMode({ c, ctx, single, refCcy, rows: viewRows, enabled, onLatest, groupBy, openDraft, onOpenRun }: {
  c: ConsolidatedView; ctx: Ctx; single: string | null; refCcy: string; rows: ConsolidatedRow[];
  enabled: boolean; onLatest: () => void;
  groupBy: { levels: GroupLevel[]; lenses: GroupLens[] };
  /** TRADE-LIST: a Trades row asked to open this draft (once per request `n`). */
  openDraft?: { id: string; ref: string; n: number } | null;
  /** TRADE-CONS 4b: open a run (by id) on its consolidation, in trade mode. */
  onOpenRun?: (runId: string) => void;
}): TradeMode {
  // TRADE-CONS 4b: a consolidation shown on its own is traded by useConsolidationTradeMode.
  const notSingleWhy = 'Trading works on one portfolio, or on one consolidation shown on its own: choose one portfolio, or tick only one consolidation.';
  const qc = useQueryClient();
  // 3b-2: securities the portfolio does not hold, added to buy (Pedro: "a new line opens, to
  // input a new security … Buy selected by default"). Each becomes a row of the table.
  const [added, setAdded] = useState<Added[]>([]);
  const addedRows = useMemo(() => added.map(addedRow), [added]);
  const rows = useMemo(() => [...addedRows, ...viewRows], [addedRows, viewRows]);
  const { registerDirty, confirmDiscardAsync } = useDirtyForm();
  const [active, setActive] = useState(false);
  // Pedro 2026-10-23: no unit dialog; trade mode opens on % of portfolio, changed with the
  // Unit buttons of the trade bar ("make the initial button selection be % of portfolio").
  const [unit, setUnit] = useState<Unit>('pct');
  // I6 (Pedro 2026-10-23): Target % — type the % wanted after the trade; a new button group
  // right of Unit ("There will be more").
  const [target, setTarget] = useState(false);
  // I1: show only the rows that carry a trade (and the cash accounts they settle in).
  const [onlyTraded, setOnlyTraded] = useState(false);
  const [std, setStd] = useState<SettleDefault>('ref');
  const [stdOther, setStdOther] = useState<string | null>(null);
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [title, setTitle] = useState('');
  const [draftId, setDraftId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<TradeDraft | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Rebalance to… state (declared here: the M-REB cash effect further down depends on it).
  const [rebBusy, setRebBusy] = useState(false);
  // M-REB (Pedro 2026-10-26): "Rebalance to..." opens a choice, Model or Portfolio, then that picker.
  const [rebPick, setRebPick] = useState<null | 'portfolio' | 'model' | 'consolidation'>(null);
  // RB-DEFAULT (Pedro 2026-10-29: "the Portfolio / Consolidation assigned Model as the default choice
  // but may wish to override"; S1-UX: a portfolio's own model "is recalled as its default every time
  // the Portfolio is Viewed or Traded by itself", in a consolidation or not): the portfolio's own
  // model is chosen when Model… opens. (The loud confirm on changing it comes with Rebalance to…
  // Consolidation, U4 step 3.)
  const assignQ = useQuery({ queryKey: ['agreement-portfolios', 'overview', ''],
    queryFn: () => agreementPortfoliosApi.overview({}), enabled: !!single && rebPick === 'model' });
  const assigned = single ? assignQ.data?.items.find((r) => r.ref === single) : undefined;
  const rebDefaultId = assigned?.model?.id ?? null;
  const rebDefaultNote = assigned?.model
    ? `${assigned.model.name} is this portfolio's default model: chosen for you; you can choose another.` : null;
  const [rebNote, setRebNote] = useState<{
    label: string; date: string | null; noIsin: number; kind: 'portfolio' | 'model' | 'consolidation';
    /** Pedro: "never silently skip anything" — what could not be done is named here. */
    problems: string[]; cash: string[];
    /** The model's cash per currency (% of the portfolio): the live lines compare the table with it. */
    modelCash?: Record<string, number>;
  } | null>(null);
  const [rebReject, setRebReject] = useState<{ label: string; offenders: { name: string; now: number; want: number }[] } | null>(null);
  /** The model's cash per currency (% of the portfolio), reached with transfers once the trades
   *  are on screen (the M-REB effect works on the trades' settled cash). */
  const [pendingCash, setPendingCash] = useState<Record<string, number> | null>(null);

  // N5 / TRADE-FLOW (Pedro 2026-10-25): only a Draft can be changed; from Email to client on it is
  // locked for everyone — an Admin too, unless they switch on the temporary Admin override (Beta).
  const override = useAdminOverride();

  const pmeta = c.portfolios.find((p) => p.ref === single);
  const portfolioCcy = pmeta?.ref_currency ?? 'EUR';
  const latest = c.dates[0];
  const isLatest = c.as_of === latest;

  const draftsQ = useQuery({ queryKey: ['trades', 'drafts', single], queryFn: () => tradesApi.list(single!),
    enabled: active && !!single });

  // The portfolio's cash accounts (one settlement column each, TQ5).
  const cash = useMemo(() => rows.filter((r) => r.is_cash && r.account_code)
    .map((r) => ({ code: r.account_code as string, ccy: r.currency, key: r.key })), [rows]);
  // TF-XFER2: a transfer's two legs in the currency shown (null: no rate on this date).
  const xRates = useMemo(() => (single ? ratesFor(single, ctx) : {}), [single, ctx]);
  const cashCcy = (code: string) => cash.find((a) => a.code === code)?.ccy ?? portfolioCcy;
  const shownOut = (t: Transfer) => convert(t.out, cashCcy(t.from), refCcy, xRates);
  const shownIn = (t: Transfer) => convert(t.inn, cashCcy(t.to), refCcy, xRates);
  const defaultAccount = (row: ConsolidatedRow): string | null => {
    if (std === 'other') return stdOther;
    const ccy = std === 'ref' ? portfolioCcy : row.currency;
    return cash.find((a) => a.ccy === ccy)?.code ?? null;
  };

  const gross = single ? grossIn(single, ctx) : null;
  // Per security row: the holding, its value per unit (accrued interest included, ACC-1), the
  // trade worked out from the entry, adjusted to the lot rules (TQ6) and the holding.
  const calc = useMemo(() => {
    const out: Record<string, {
      side: Side | null; held: number; unitValue: number | null; q: number; note: string | null; capped: boolean; value: number;
      notApprovedKept: boolean; price: number | null; accruedPerUnit: number | null;
    }> = {};
    if (!single) return out;
    const rates = ratesFor(single, ctx);
    const addedBy = Object.fromEntries(added.map((a) => [`${NEW_PREFIX}${a.sec.id}`, a]));
    for (const r of rows) {
      if (r.is_cash || r.is_loan) continue;
      const a = addedBy[r.key];
      const held = a ? 0 : (measure(r, single, 'quantity', ctx) ?? 0);
      const v0 = a ? null : measure(r, single, 'value_ref', ctx);
      // A security added to buy: its value per unit = price × factor (0.01 per 100 nominal) in
      // its currency + accrued interest per unit, both in the currency shown (the custodian's
      // rates). A bond with no accrued interest known cannot be valued (TQ9: type it).
      let unitValue: number | null = held > 0 && v0 != null ? v0 / held : null;
      let addedAccrued: number | null = null;
      if (a) {
        const p = n(a.price);
        const clean = p != null && p > 0 ? convert(p * a.factor, a.sec.currency, refCcy, rates) : null;
        const acc = a.accrued.trim() !== '' ? convert(Number(a.accrued), a.accruedCcy, refCcy, rates)
          : (a.factor < 1 ? null : 0);
        unitValue = clean != null && acc != null ? clean + acc : null;
        addedAccrued = a.accrued.trim() !== '' ? convert(Number(a.accrued), a.accruedCcy, portfolioCcy, rates) : (a.factor < 1 ? null : 0);
      }
      const e = entries[r.key] ?? EMPTY;
      let q = 0, note: string | null = null, capped = false;
      // The side: ticked by the user; with Target % (I6), worked out from the target.
      let side: Side | null = target ? null : e.side;
      if (e.amount.trim() !== '' && (target || e.side)) {
        let raw: number | null = null;
        if (target) {
          // I6 (Pedro 2026-10-23): type the % wanted after the trade; buy or sell the difference.
          const t = targetTrade(Number(e.amount), held, unitValue, gross);
          if (t) {
            side = t.side;
            raw = t.side ? t.quantity : null;
            // TP-LOT2: only a 0 % target sells the whole holding (no stub left by rounding).
            if (side === 'sell' && held > 0 && targetSellsAll(Number(e.amount))) raw = held;
            if (side === 'buy' && !r.authorized) { raw = null; note = 'not approved: it can only be reduced, not increased'; }
          } else note = 'cannot be worked out (no value for this line)';
        } else {
          raw = toQuantity(Number(e.amount), unit, unitValue, gross);
          if (raw == null) note = 'cannot be worked out (no value for this line)';
        }
        // TF-CAP100 (Pedro 2026-10-25: "% of portfolio buy trades can exceed 100% … This should not
        // be possible. It should be capped at source"): a buy never takes the security above 100%
        // of the portfolio (its gross value now), in any unit; the lot rules then round down.
        if (raw != null && side === 'buy' && unitValue != null && unitValue > 0 && gross) {
          const maxQ = Math.max(0, gross / unitValue - held);
          if (raw > maxQ + 1e-9) { raw = maxQ; capped = true; }
        }
        if (raw != null && side) {
          const adj = adjust(raw, side, held, { minPiece: n(r.min_piece), minIncrement: n(r.min_increment) }, !target && unit === 'quantity');
          q = adj.quantity; note = adj.note;
          if (capped) note = `capped: at most 100% of the portfolio${note ? `; ${note}` : ''}`;
        }
      }
      const line = r.by_portfolio[single];
      const accrued = n(line?.accrued_ref);
      out[r.key] = {
        side, held, unitValue, q, note, capped, value: unitValue != null ? q * unitValue : 0,
        // T5 (a) / TQ8: a held not-approved security that is not sold in full needs a reason.
        notApprovedKept: !a && !r.authorized && !!r.security_id && held > 0 && !(side === 'sell' && q >= held - 1e-9),
        price: a ? n(a.price) : n(line?.price),
        // Accrued interest per unit in the PORTFOLIO's reference currency (saved with the line).
        accruedPerUnit: a ? addedAccrued : (accrued != null && held > 0 ? accrued / held : null),
      };
    }
    return out;
  }, [rows, entries, unit, target, ctx, single, gross, added, refCcy, portfolioCcy]);

  // Cash after the trades (in the shown currency): each trade's value comes out of (buy) or
  // goes into (sale) the account it settles in; a transfer (TP-XFER) moves cash between two.
  const cashMove = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of rows) {
      const k = calc[r.key], e = entries[r.key];
      if (!k || !k.side || !e?.settle || k.q <= 0) continue;
      m[e.settle] = (m[e.settle] ?? 0) + (k.side === 'buy' ? -k.value : k.value);
    }
    for (const t of transfers) {
      m[t.from] = (m[t.from] ?? 0) - (shownOut(t) ?? 0);
      m[t.to] = (m[t.to] ?? 0) + (shownIn(t) ?? 0);
    }
    return m;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, calc, entries, transfers, xRates, cash, refCcy]);
  const newValue = (r: ConsolidatedRow): number | null => {
    if (!single) return null;
    const v0 = measure(r, single, 'value_ref', ctx);
    if (r.is_cash) return v0 == null ? null : v0 + (cashMove[r.account_code ?? ''] ?? 0);
    const k = calc[r.key];
    if (!k) return v0;
    const dq = k.side ? (k.side === 'buy' ? k.q : -k.q) : 0;
    if (r.key.startsWith(NEW_PREFIX)) return k.unitValue == null ? (dq ? null : 0) : dq * k.unitValue;
    return k.unitValue == null ? v0 : (k.held + dq) * k.unitValue;
  };
  const newGross = useMemo(() => {
    let s = 0;
    for (const r of rows) {
      if (r.is_loan) continue;
      const v = newValue(r);
      if (v == null) return null;
      s += v;
    }
    return s;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, calc, cashMove, ctx, single]);

  const setEntry = (key: string, patch: Partial<Entry>) => {
    setEntries((p) => ({ ...p, [key]: { ...(p[key] ?? EMPTY), ...patch } }));
    setDirty(true);
  };
  // Target % of a held not-approved security: its weight now is the ceiling (Pedro 2026-10-23).
  // Null when the row is not one (approved, new, not in Securities) or has no value.
  const notApprovedWeight = (row: ConsolidatedRow): number | null => {
    if (row.authorized || !row.security_id || row.key.startsWith(NEW_PREFIX) || !single || !gross) return null;
    if ((calc[row.key]?.held ?? 0) <= 0) return null;
    const v0 = measure(row, single, 'value_ref', ctx);
    return v0 == null ? null : (v0 / gross) * 100;
  };
  const focusAmount = useRef<Record<string, string>>({}).current;   // an amount as it was on entering the field
  const setSide = (row: ConsolidatedRow, side: Side) => {
    const e = entries[row.key] ?? EMPTY;
    const next = e.side === side ? null : side;
    // TRADE-CONS 4b smoke (Pedro 2026-10-30): unticking empties the amount (as in a consolidation).
    setEntry(row.key, { side: next, settle: next ? (e.settle ?? defaultAccount(row)) : e.settle, ...(next ? {} : { amount: '' }) });
  };
  // TQ5: the standard choice pre-fills every trade's settlement account; each can then be changed.
  // SETTLE-BUG (Pedro 2026-10-30 smoke): a line traded through Target % (e.g. after Rebalance to…)
  // has its Buy / Sell worked out (`calc`), not stored (`e.side` null) — it must be re-filled too,
  // or changing Settle in silently leaves it without an account.
  const applyStd = (s: SettleDefault, other: string | null) => {
    setStd(s); setStdOther(other);
    setEntries((p) => Object.fromEntries(Object.entries(p).map(([k, e]) => {
      const row = rows.find((r) => r.key === k);
      if (!row || !(e.side || calc[k]?.side)) return [k, e];
      const ccy = s === 'ref' ? portfolioCcy : row.currency;
      return [k, { ...e, settle: s === 'other' ? other : (cash.find((a) => a.ccy === ccy)?.code ?? null) }];
    })));
    setDirty(true);
  };
  // TQ2: a new unit converts every entry (from the quantity it stood for, before any lot rounding).
  const changeUnit = (u: Unit) => {
    setEntries((p) => Object.fromEntries(Object.entries(p).map(([k, e]) => {
      const k2 = calc[k];
      if (!k2 || e.amount.trim() === '') return [k, e];
      const raw = toQuantity(Number(e.amount), unit, k2.unitValue, gross);
      const back = raw == null ? null : fromQuantity(raw, u, k2.unitValue, gross);
      return [k, { ...e, amount: back == null ? '' : String(Number(back.toPrecision(12))) }];
    })));
    setUnit(u);
    setDirty(true);
  };

  useEffect(() => registerDirty('trade-mode', active && dirty), [active, dirty, registerDirty]);

  // ── adding a security not held (3b-2) ────────────────────────────────────
  const secsQ = useQuery({ queryKey: ['securities', 'list', 'all'], queryFn: () => securitiesApi.list({}),
    enabled: active && !!single });
  const heldIds = useMemo(() => new Set(viewRows.map((r) => r.security_id).filter(Boolean)), [viewRows]);
  // T5: only Approved securities may be bought; the search matches name, ISIN, BB Ticker, FIGI.
  const buyable = useMemo(() => (secsQ.data?.items ?? [])
    .filter((s) => s.authorized && !heldIds.has(s.id) && !added.some((a) => a.sec.id === s.id))
    .map((s) => ({ value: s.id, label: [s.isin, s.description, s.bb_ticker, s.figi, s.currency].filter(Boolean).join(' · ') })),
  [secsQ.data, heldIds, added]);
  const [adding, setAdding] = useState(false);
  const addSecurity = async (id: string | null, saved?: { price: string | null; source: PriceSource | null; date: string | null; accruedRef: number | null }) => {
    const sec = (secsQ.data?.items ?? []).find((s) => s.id === id) ?? (id ? await securitiesApi.get(id) : null);
    if (!sec || !single) return null;
    setAdding(true);
    try {
      // TQ3 / TQ3a: the price from the first rung that has one; TQ9: accrued interest per unit
      // borrowed from the same line (later calculated from the bond's data, TRADE-ACC).
      const p = await tradesApi.price(single, sec.id);
      const a: Added = {
        sec, price: saved?.price ?? p.price ?? '', source: saved ? saved.source : (p.source as PriceSource | null),
        priceDate: saved ? saved.date : (p.source ? p.date : null), factor: Number(p.factor ?? 1), factorGuessed: p.factor_guessed,
        accrued: saved ? (saved.accruedRef == null ? '' : clean(saved.accruedRef)) : (p.accrued_per_unit ?? ''),
        accruedCcy: saved ? portfolioCcy : (p.accrued_currency ?? portfolioCcy),
      };
      setAdded((prev) => [...prev, a]);
      return a;
    } catch (e) {
      notifications.show({ color: 'red', title: 'Price not found', message: errMsg(e) });
      return null;
    } finally { setAdding(false); }
  };
  const pickToBuy = async (id: string | null) => {
    const a = await addSecurity(id);
    if (!a) return;
    const row = addedRow(a);
    // Buy selected by default (Pedro); settled per the standard choice.
    setEntries((p) => ({ ...p, [row.key]: { ...EMPTY, side: 'buy', settle: defaultAccount(row) } }));
    setDirty(true);
  };
  const removeAdded = (key: string) => {
    setAdded((p) => p.filter((a) => `${NEW_PREFIX}${a.sec.id}` !== key));
    setEntries((p) => { const x = { ...p }; delete x[key]; return x; });
    setDirty(true);
  };
  const editAdded = (key: string, patch: Partial<Added>) => {
    setAdded((p) => p.map((a) => (`${NEW_PREFIX}${a.sec.id}` === key ? { ...a, ...patch } : a)));
    setDirty(true);
  };
  const searchCell = !active || !single ? null : (
    <Tooltip withArrow multiline w={320} label="Buy a security the portfolio does not hold: type part of its name, ISIN, BB Ticker or FIGI. Approved securities only.">
      <Select size="xs" placeholder={adding ? 'Finding a price…' : '+ Buy a new security (name, ISIN, BB Ticker, FIGI)'}
        data={buyable} searchable clearable value={null} limit={50} disabled={adding}
        nothingFoundMessage={secsQ.isLoading ? 'Loading…' : 'No Approved security matches (or it is already in the portfolio)'}
        onChange={(v) => void pickToBuy(v)} comboboxProps={{ withinPortal: true }} />
    </Tooltip>
  );

  const reset = () => { setEntries({}); setAdded([]); setTransfers([]); setTitle(''); setDraftId(null); setDirty(false); setLastSaved(null); };
  const enter = () => {
    if (!isLatest) onLatest();   // TQ11: trades overlay the latest positions
    reset(); setUnit('pct'); setActive(true);
  };
  const leave = async () => {
    if (dirty && !(await confirmDiscardAsync())) return;
    reset(); setActive(false);
  };

  const loadDraft = async (id: string | null) => {
    if (dirty && !(await confirmDiscardAsync())) return;
    if (!id) { reset(); return; }
    try {
      const d = await tradesApi.get(id);
      const next: Record<string, Entry> = {};
      const xfers: Transfer[] = [];
      setAdded([]);
      for (const ln of d.lines ?? []) {
        if (ln.side === 'transfer') {
          // TF-XFER2: the two legs as saved, each in its account's currency.
          const out = n(ln.quantity), inn = n(ln.to_amount);
          if (ln.settle_account && ln.to_account && out != null && inn != null) xfers.push({ from: ln.settle_account, to: ln.to_account, out, inn });
          continue;
        }
        const row = viewRows.find((r) => r.security_id && r.security_id === ln.security_id);
        if (!row) {
          // A security the portfolio does not hold (3b-2): back as an added row, at its saved price.
          if (!ln.security_id || ln.side !== 'buy') continue;
          const q = n(ln.quantity);
          const acc = n(ln.accrued_ref);
          const a = await addSecurity(ln.security_id, { price: ln.price ?? null, source: ln.price_source ?? null,
            date: ln.price_date ?? null, accruedRef: acc != null && q ? acc / q : null });
          if (a) next[`${NEW_PREFIX}${a.sec.id}`] = { side: 'buy', settle: ln.settle_account ?? null, note: '',
            amount: d.unit === 'quantity' ? (ln.quantity ?? '') : (ln.amount_entered ?? '') };
          continue;
        }
        // The saved quantity is what counts; shown again in the draft's unit at today's view
        // (the currency shown may differ from when it was saved).
        let amt = ln.amount_entered ?? '';
        const q = n(ln.quantity);
        if (q != null && single) {
          const held = measure(row, single, 'quantity', ctx) ?? 0;
          const v0 = measure(row, single, 'value_ref', ctx);
          const back = fromQuantity(q, d.unit, held > 0 && v0 != null ? v0 / held : null, gross);
          if (back != null) amt = clean(back);
        }
        next[row.key] = { side: (ln.side as Side | null) ?? null, amount: amt, settle: ln.settle_account ?? null, note: ln.keep_note ?? '' };
      }
      setUnit(d.unit); setStd(d.settle_default); setStdOther(d.settle_default_account);
      setEntries(next); setTransfers(xfers); setTitle(d.title ?? ''); setDraftId(d.id); setDirty(false); setLastSaved(d);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Draft not opened', message: errMsg(e) });
    }
  };

  // TRADE-LIST: a Trades row opens its draft in trade mode once the portfolio is on screen
  // (latest positions, TQ11). Handled once per request.
  const openedRef = useRef<number | null>(null);
  useEffect(() => {
    if (!openDraft || !enabled || openedRef.current === openDraft.n || single !== openDraft.ref) return;
    if (!isLatest) { onLatest(); return; }
    openedRef.current = openDraft.n;
    setActive(true);
    void loadDraft(openDraft.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDraft?.n, openDraft?.id, enabled, single, isLatest]);

  /** What must be fixed before saving (shown, never silently dropped). M-REB-LOUD (Pedro
   *  2026-10-26: "we need a louder warning"): the rows concerned are kept in `blockedKeys`, so
   *  they show red too. */
  const [blockers, blockedKeys] = useMemo(() => {
    const out: string[] = [];
    const keys = new Set<string>();
    const add = (key: string, msg: string) => { out.push(msg); keys.add(key); };
    for (const r of rows) {
      const k = calc[r.key], e = entries[r.key];
      if (!k) continue;
      const typed = (e?.amount ?? '').trim() !== '';
      if ((k.side || (target && typed)) && typed && k.q <= 0 && k.note) add(r.key, `${r.name}: nothing to trade (${k.note})`);
      else if (k.side && typed && k.q <= 0 && !target) add(r.key, `${r.name}: nothing to trade (amount too small)`);
      if (k.side && k.q > 0 && !e?.settle) add(r.key, `${r.name}: choose the cash account to settle it`);
      if (k.side && k.q > 0 && k.price == null && !r.key.startsWith(NEW_PREFIX)) add(r.key, `${r.name}: no price, so no value; the trade cannot be accepted`);
      if (k.notApprovedKept && !(e?.note ?? '').trim()) add(r.key, `${r.name}: not approved and kept — give the reason`);
      if (r.key.startsWith(NEW_PREFIX) && typed && k.unitValue == null) {
        const a = added.find((x) => `${NEW_PREFIX}${x.sec.id}` === r.key);
        add(r.key, !a || !n(a.price)
          ? `${r.name}: no price found anywhere — type one, or the trade cannot be accepted`
          : `${r.name}: accrued interest per unit unknown — type it (0 if none)`);
      }
    }
    // TP-XFER: a transfer needs a rate between its accounts' currencies and the one shown.
    if (single) {
      const rates = ratesFor(single, ctx);
      for (const t of transfers) {
        const a = cash.find((x) => x.code === t.from), b = cash.find((x) => x.code === t.to);
        if (!a || !b || convert(1, refCcy, a.ccy, rates) == null || convert(1, refCcy, b.ccy, rates) == null)
          out.push(`Transfer ${t.from} → ${t.to}: no exchange rate for its currencies on this date`);
      }
    }
    return [out, keys] as const;
  }, [rows, calc, entries, added, target, transfers, cash, single, ctx, refCcy]);

  const body = (): TradeDraftIn => {
    const lines: TradeLineIn[] = [];
    for (const r of rows) {
      const k = calc[r.key], e = entries[r.key];
      if (!k || !r.security_id) continue;
      const note = (e?.note ?? '').trim() || null;
      if (k.side && k.q > 0) {
        const l = r.by_portfolio[single!];
        const a = added.find((x) => `${NEW_PREFIX}${x.sec.id}` === r.key);
        // With Target % the draft is saved in % of portfolio (the traded %, signed by side).
        const amt = target ? clean(fromQuantity(k.q, 'pct', k.unitValue, gross) ?? 0) : (e?.amount ?? '');
        lines.push({ security_id: r.security_id, side: k.side, amount_entered: amt, quantity: clean(k.q),
          price: a ? a.price : (l?.price ?? null), price_source: a ? (a.source ?? 'typed') : 'own',
          price_date: a ? (a.priceDate ?? c.as_of) : c.as_of,
          accrued_ref: k.accruedPerUnit != null ? clean(k.accruedPerUnit * k.q) : null,
          settle_account: e?.settle ?? null, keep_note: k.notApprovedKept ? note : null });
      } else if (k.notApprovedKept && note) {
        lines.push({ security_id: r.security_id, side: null, keep_note: note });
      }
    }
    // TF-XFER2: each transfer leaves its account (out, that account's currency) and arrives in the
    // other (inn, its currency); amount_entered keeps the leg out in the reference currency.
    for (const t of transfers) {
      const refAmt = convert(t.out, cashCcy(t.from), portfolioCcy, xRates);
      lines.push({ security_id: null, side: 'transfer', settle_account: t.from, to_account: t.to,
        quantity: clean(t.out), to_amount: clean(t.inn), amount_entered: refAmt == null ? null : clean(refAmt) });
    }
    return { portfolio: single!, base_draft_id: null, unit: target ? 'pct' : unit, settle_default: std,
      settle_default_account: std === 'other' ? stdOther : null, title: title.trim() || null, lines };
  };
  // I5: Save warns (Continue / Stay and correct) when a cash account would go negative.
  const [negWarn, setNegWarn] = useState<{ asNew: boolean } | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const overdrawn = cash.map((a) => {
    const row = rows.find((r) => r.key === a.key);
    return { code: a.code, value: row ? newValue(row) : null };
  }).filter((o) => o.value != null && o.value < -0.005 && (cashMove[o.code] ?? 0) < 0) as { code: string; value: number }[];
  const askSave = (asNew: boolean) => { if (overdrawn.length) setNegWarn({ asNew }); else void save(asNew); };

  // ── TP-XFER (Pedro 2026-10-24): an overdraft offers its fix before saving: "Change settlement
  // account for some trades" or "Transfer cash from another cash account" ───────────────────
  const [settleOpen, setSettleOpen] = useState(false);
  const [xferOpen, setXferOpen] = useState(false);
  const [xferRows, setXferRows] = useState<XferRow[]>([]);
  // TF-XFER2: opened from a cash row's Buy / Sell tick, the window edits that account's transfers
  // only (the others are kept); from the overdraft warning, every transfer (scope null).
  const [xferScope, setXferScope] = useState<string | null>(null);
  const cashLabel = (code: string) => `${code} (${cash.find((a) => a.code === code)?.ccy ?? '?'})`;
  const cashOptions = cash.map((a) => ({ value: a.code, label: cashLabel(a.code) }));
  const moveOn = (code: string, t: Transfer) => (t.to === code ? (shownIn(t) ?? 0) : t.from === code ? -(shownOut(t) ?? 0) : 0);
  /** Each cash account after the trades but before any transfer (shown currency). */
  const afterTrades = (code: string): number | null => {
    const a = cash.find((x) => x.code === code);
    const row = a ? rows.find((r) => r.key === a.key) : null;
    const v = row ? newValue(row) : null;
    if (v == null) return null;
    return v - transfers.reduce((s, t) => s + moveOn(code, t), 0);
  };
  const projected = (code: string, set: Transfer[]) => {
    const base = afterTrades(code);
    return base == null ? null : base + set.reduce((s, t) => s + moveOn(code, t), 0);
  };
  const round2 = (v: number | null) => (v == null ? '' : clean(Math.round(v * 100) / 100));
  /** TF-IN-RO (Pedro 2026-10-25: "have the In box update with the amount being input in the Out box,
   *  as it is being typed. That way, that field can remain read-only"): In is always Out at the
   *  custodian's rate (equal in the same currency), recomputed on every keystroke and on reopening. */
  const withInn = (r: XferRow): XferRow => {
    if (!r.from || !r.to) return { ...r, inn: '' };
    const o = Number(r.out.replace(',', '.'));
    if (!(o > 0)) return { ...r, inn: '' };
    if (cashCcy(r.from) === cashCcy(r.to)) return { ...r, inn: r.out };
    return { ...r, inn: round2(convert(o, cashCcy(r.from), cashCcy(r.to), xRates)) };
  };
  const toRow = (t: Transfer): XferRow => withInn({ from: t.from, to: t.to, out: clean(t.out), inn: '' });
  const editXfer = (i: number, patch: Partial<XferRow>) =>
    setXferRows((p) => p.map((x, j) => (j === i ? withInn({ ...x, ...patch }) : x)));
  const openTransfers = (scope?: string) => {
    if (scope) {
      // TF-XFER3 (Pedro 2026-10-25: "only Sell ticks on each Cash row and the modal always keeps
      // that account as the From: account"): this account's transfers out, or a first new one.
      const mine = transfers.filter((t) => t.from === scope).map(toRow);
      setXferRows(mine.length ? mine : [{ from: scope, to: null, out: '', inn: '' }]);
      setXferScope(scope);
    } else {
      // The overdraft warning: every transfer, then a suggested split for what is still overdrawn.
      const needs = cash.map((a) => ({ code: a.code, ccy: a.ccy, need: -(projected(a.code, transfers) ?? 0) })).filter((x) => x.need > 0.005);
      const spares = cash.map((a) => ({ code: a.code, ccy: a.ccy, spare: projected(a.code, transfers) ?? 0 }));
      const sugg = suggestTransfers(needs, spares).map((s) => withInn({ from: s.from, to: s.to, inn: '',
        out: round2(convert(s.amount, refCcy, cashCcy(s.from), xRates)) }));
      setXferRows([...transfers.map(toRow), ...sugg]);
      setXferScope(null);
    }
    setNegWarn(null); setXferOpen(true);
  };
  const num = (s: string) => Number(s.replace(',', '.'));
  const xferParsed: Transfer[] = xferRows.filter((r) => r.from && r.to && num(r.out) > 0 && num(r.inn) > 0)
    .map((r) => ({ from: r.from!, to: r.to!, out: num(r.out), inn: num(r.inn) }));
  const xferProblems = xferRows.flatMap((r, i) => (!r.from || !r.to ? [`Transfer ${i + 1}: choose both accounts`]
    : r.from === r.to ? [`Transfer ${i + 1}: from and to are the same account`]
      : !(num(r.out) > 0) ? [`Transfer ${i + 1}: the amount out must be above 0`]
        : !(num(r.inn) > 0) ? [`Transfer ${i + 1}: no exchange rate between these accounts' currencies on this date`] : []));
  /** The transfers the window does not show (scoped: every other account's transfers out). */
  const xferKept = xferScope ? transfers.filter((t) => t.from !== xferScope) : [];
  /** TF-XFER3: transfers between the same two accounts IN THE SAME DIRECTION join into one (Out and
   *  In added); the opposite direction stays its own transfer (another FX trade), warned below. */
  const merge = (list: Transfer[]): Transfer[] => {
    const by = new Map<string, Transfer>();
    for (const t of list) {
      const k = `${t.from}>${t.to}`, p = by.get(k);
      by.set(k, p ? { ...p, out: p.out + t.out, inn: p.inn + t.inn } : { ...t });
    }
    return [...by.values()];
  };
  const xferResult = merge([...xferKept, ...xferParsed]);
  const xferOpposite = xferResult.filter((t) => t.from < t.to && xferResult.some((u) => u.from === t.to && u.to === t.from))
    .map((t) => `${t.from} and ${t.to} send cash to each other: two separate transfers (two FX trades). To move less, reduce one of them instead.`);
  const applyTransfers = () => {
    setTransfers(xferResult);
    setDirty(true); setXferOpen(false);
  };
  // M-REB (Pedro 2026-10-26: "set cash using transfers; app already allows user to edit or bin
  // these"): once the model's trades are on screen, each currency's cash is brought to the model's
  // % of the portfolio (gross now) with suggested transfers, as the overdraft fix suggests them.
  // Anything that cannot be done is named in the rebalance note, never skipped silently.
  useEffect(() => {
    if (!pendingCash || rebBusy || !single) return;
    const cashPct = pendingCash;
    setPendingCash(null);
    const balances = cash.map((a) => ({ code: a.code, ccy: a.ccy, balance: afterTrades(a.code) }));
    const note = (problems: string[], lines: string[]) =>
      setRebNote((p) => (p ? { ...p, problems: [...p.problems, ...problems], cash: lines, modelCash: cashPct } : p));
    const modelLines: string[] = [];
    if (!gross || balances.some((b) => b.balance == null)) {
      note(['The cash after the trades could not be worked out (a value or a rate is missing), so no cash transfer was suggested.'], modelLines);
      return;
    }
    const accounts = balances as { code: string; ccy: string; balance: number }[];
    const modelTargets = Object.fromEntries(Object.entries(cashPct).map(([c, p]) => [c, (p / 100) * gross]));
    // MR-RES (Pedro 2026-10-26: "Yes, empty currencies the model doesn't hold"): the excess the
    // lot rules leave goes to the model's main cash currency, so the others are emptied in full.
    const sw = sweepExcess(accounts, modelTargets);
    const plan = planCashTargets(accounts, sw.targets);
    const sugg = suggestTransfers(plan.needs, plan.spares);
    const made: Transfer[] = [];
    const problems: string[] = [];
    const sweepLines: string[] = [];
    // MR-RES: where any excess cash goes (the amounts themselves are shown live, below the note).
    if (sw.into) {
      sweepLines.push(`Every currency the model does not hold is emptied into Cash ${sw.into}; any cash above the model's (left by the lot rules, or by a line that could not be bought) ends there.`);
    } else if (sw.excess > 0.005) {
      problems.push(`${amount(sw.excess)} ${refCcy} more cash than the model holds is left after the trades, and none of the model's cash currencies has an account here: that cash stays where the trades leave it.`);
    }
    for (const s of sugg) {
      const out = convert(s.amount, refCcy, cashCcy(s.from), xRates);
      const row = out == null ? null : withInn({ from: s.from, to: s.to, out: round2(out), inn: '' });
      if (!row || !(num(row.out) > 0) || !(num(row.inn) > 0)) {
        problems.push(`No exchange rate on this date to move cash from ${cashLabel(s.from)} to ${cashLabel(s.to)}: ${amount(s.amount)} ${refCcy} was not transferred.`);
        continue;
      }
      made.push({ from: s.from, to: s.to, out: num(row.out), inn: num(row.inn) });
    }
    for (const nd of plan.needs) {
      const got = sugg.filter((s) => s.to === nd.code).reduce((t, s) => t + s.amount, 0);
      if (nd.need - got > 0.005) {
        problems.push(`Cash ${nd.ccy}: ${amount(nd.need - got)} ${refCcy} short of the model's ${pctFmt(cashPct[nd.ccy] ?? 0)}: not enough cash to spare in the other accounts.`);
      }
    }
    for (const c of plan.missing) {
      problems.push(`The model holds Cash ${c} (${pctFmt(cashPct[c])}) but this portfolio has no ${c} cash account: ${sw.into
        ? `that share goes to Cash ${sw.into} instead (counted in its excess above).`
        : 'that cash stays where the trades leave it.'}`);
    }
    setTransfers(merge(made));
    note(problems, [...modelLines, ...sweepLines, made.length
      ? `${made.length} cash transfer(s) suggested to reach it: see the cash rows (edit or bin them like any transfer).`
      : 'No cash transfer needed.']);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCash, rebBusy, calc, cashMove]);
  // M-REB-LOUD, live (Pedro's smoke 2026-10-26: after he typed the missing price the note still
  // read "Cash EUR ends 55,875.32 EUR above…"; then "why does it say … have changed since … if the
  // issue has been resolved?"): the note's cash lines and its "could not be bought" line are worked
  // out from the table on every render, so they are always the current state.
  const liveNote = useMemo(() => {
    if (!rebNote || rebNote.kind !== 'model' || !rebNote.modelCash || !gross) return null;
    const lines: string[] = [];
    const problems: string[] = [];
    const after = (ccy: string): number | null => {
      let s = 0;
      for (const a of cash.filter((x) => x.ccy === ccy)) {
        const row = rows.find((r) => r.key === a.key);
        const v = row ? newValue(row) : null;
        if (v == null) return null;
        s += v;
      }
      return s;
    };
    const ccys = [...new Set([...Object.keys(rebNote.modelCash), ...cash.map((a) => a.ccy)])];
    for (const c of ccys) {
      const want = rebNote.modelCash[c];
      const v = after(c);
      const now = v == null ? '-' : `${pctFmt((v / gross) * 100)} (${amount(v)} ${refCcy})`;
      if (want != null) lines.push(`Cash ${c}: ${pctFmt(want)} in the model · ${now} after these trades.`);
      else if (v != null && Math.abs(v) > 0.005) lines.push(`Cash ${c}: not in the model · ${now} after these trades.`);
    }
    const untraded = rows.filter((r) => r.key.startsWith(NEW_PREFIX) && blockedKeys.has(r.key)
      && (calc[r.key]?.q ?? 0) <= 0 && Number(entries[r.key]?.amount ?? 0) > 0);
    const pct = untraded.reduce((s, r) => s + Number(entries[r.key]?.amount ?? 0), 0);
    if (untraded.length) {
      problems.push(`${pctFmt(pct)} of the model could not be bought: ${untraded.map((r) => r.name).join(', ')} (see "To fix before saving": type the price). Until then that share stays in cash.`);
    }
    return { lines, problems };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rebNote, rows, calc, cashMove, entries, blockedKeys, gross]);
  const notePlain = (rebNote?.problems.length ?? 0) === 0 && (liveNote?.problems.length ?? 0) === 0;
  /** TF-XFER2: an account's transfers out as a comparable string (orange tick while it differs from the saved draft). */
  const xferSig = (code: string, list: Transfer[]) => list.filter((t) => t.from === code)
    .map((t) => `${t.from}>${t.to}:${t.out.toFixed(2)}:${t.inn.toFixed(2)}`).sort().join('|');
  const savedTransfers: Transfer[] = (lastSaved?.lines ?? []).filter((l) => l.side === 'transfer' && l.settle_account && l.to_account)
    .map((l) => ({ from: l.settle_account!, to: l.to_account!, out: n(l.quantity) ?? 0, inn: n(l.to_amount) ?? 0 }));
  /** The trades settling in an overdrawn account (the settlement fix lists them). */
  const settleRows = rows.filter((r) => {
    const k = calc[r.key], e = entries[r.key];
    return !!k?.side && k.q > 0 && !!e?.settle && overdrawn.some((o) => o.code === e.settle);
  });
  // I6: switching Target % on shows each traded row's target (its New %); off, the amounts again.
  const toggleTarget = (on: boolean) => {
    setEntries((p) => Object.fromEntries(Object.entries(p).map(([key, e]) => {
      const k = calc[key];
      if (!k) return [key, e];
      if (on) {
        const row = rows.find((r) => r.key === key);
        const v = k.side && k.q > 0 && row ? newValue(row) : null;
        return [key, { ...e, amount: v != null && gross ? clean((v / gross) * 100) : '' }];
      }
      const back = k.side && k.q > 0 ? fromQuantity(k.q, unit, k.unitValue, gross) : null;
      return [key, { ...e, side: k.side ?? null, amount: back == null ? '' : clean(back) }];
    })));
    setTarget(on);
    setDirty(true);
  };
  // ── Rebalance to… (TRADE-REB, Pedro 2026-10-23: "showing a portfolio picker; upon choosing that
  // target portfolio, the portfolio being traded instantly acquires and shows in its Trade mode
  // table all the trades necessary to achieve the target positions") ────────────────────────
  // Each security's Target % = its share of the chosen portfolio's gross (0 = sell it all);
  // cash is what is left, loans are untouched. A target holding more of a not-approved security
  // than this portfolio is refused as a whole (rebReject).
  /** The shared core of Rebalance to… (a portfolio or a model): every security's Target % =
   *  `pctBy` (0 for the ones not there, sold), through the Target % path (lots, increments, no
   *  short sales, the 100 % cap). Refused as a whole when the target holds a not-approved security
   *  at a higher weight than this portfolio (Pedro 2026-10-23, kept for models, RQ3). */
  const applyRebalance = async (label: string, kind: 'portfolio' | 'model' | 'consolidation', pctBy: Record<string, number>,
    meta: Record<string, { name: string; authorized: boolean }>, info: { date: string | null; noIsin: number }) => {
    if (!single) return false;
    // Pedro 2026-10-23: a target that includes any security "simultaneously a) not approved
    // and b) [with a] higher weight than the portfolio being traded" rejects "the entire
    // exercise … with a centre screen warning explaining why". Weights compared as shown
    // (2 decimals); a security this portfolio does not hold weighs 0 here.
    const weightNow = (sid: string) => viewRows.reduce((s, r) => {
      if (r.is_cash || r.is_loan || r.security_id !== sid || !gross) return s;
      return s + ((measure(r, single, 'value_ref', ctx) ?? 0) / gross) * 100;
    }, 0);
    const offenders = Object.entries(pctBy).filter(([sid]) => !meta[sid].authorized)
      .map(([sid, want]) => ({ name: meta[sid].name, now: weightNow(sid), want }))
      .filter((o) => o.want > o.now + 0.005);
    if (offenders.length) {
      setRebReject({ label, offenders });
      return false;
    }
    const next: Record<string, Entry> = {};
    for (const r of viewRows) {
      if (r.is_cash || r.is_loan || !r.security_id) continue;
      next[r.key] = { side: null, amount: clean(pctBy[r.security_id] ?? 0), settle: defaultAccount(r), note: entries[r.key]?.note ?? '' };
    }
    setAdded([]);
    const problems: string[] = [];
    for (const [sid, p] of Object.entries(pctBy)) {
      if (heldIds.has(sid)) continue;   // every one left is Approved (the rule above)
      const a = await addSecurity(sid);
      if (a) next[`${NEW_PREFIX}${sid}`] = { side: null, amount: clean(p), settle: defaultAccount(addedRow(a)), note: '' };
      // Never silently skipped (Pedro 2026-10-26): a security that could not be added is named.
      else problems.push(`${meta[sid].name} (${pctFmt(p)}) was not added: no price was found. Add it with "+ Buy a new security" and type its price.`);
    }
    setEntries(next); setTarget(true); setDirty(true);
    setRebNote({ label, date: info.date, noIsin: info.noIsin, kind, problems, cash: [] });
    return true;
  };

  const rebalanceTo = async (targetRef: string | null) => {
    if (!targetRef || !single) return;
    setRebBusy(true);
    try {
      const t = await portfoliosApi.consolidated([targetRef], {});
      const pctBy: Record<string, number> = {};
      const meta: Record<string, { name: string; authorized: boolean }> = {};
      let noIsin = 0;
      for (const r of t.rows) {
        if (r.is_cash || r.is_loan) continue;
        if (!r.security_id) { noIsin++; continue; }
        const p = n(r.by_portfolio[targetRef]?.pct);
        if (p == null || p <= 0) continue;
        pctBy[r.security_id] = (pctBy[r.security_id] ?? 0) + p;
        meta[r.security_id] = { name: r.name, authorized: !!r.authorized };
      }
      const tp = t.portfolios.find((p) => p.ref === targetRef);
      const label = tp ? `${tp.bank_mnemonic ?? 'No bank'} · ${tp.portfolio_number}` : targetRef;
      await applyRebalance(label, 'portfolio', pctBy, meta, { date: t.as_of, noIsin });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Rebalance not done', message: errMsg(e), autoClose: false });
    } finally { setRebBusy(false); }
  };

  /** TRADE-CONS U4 step 3 (Pedro 2026-10-29: "Rebalance to now shows Model, Portfolio and
   *  Consolidation"): copy a consolidation — each security's Target % = its total value across the
   *  consolidation's portfolios ÷ their combined gross (both in EUR, as View portfolio totals
   *  portfolios), on their latest positions. A value or gross without a rate is never guessed: the
   *  rebalance is refused, naming what is missing. */
  const rebalanceToConsolidation = async (cons: Consolidation) => {
    if (!single) return;
    setRebBusy(true);
    try {
      const refs = cons.members.map((m) => m.ref);
      const t = await portfoliosApi.consolidated(refs, {});
      const missing: string[] = [];
      let grossEur = 0;
      for (const p of t.portfolios) {
        const g = n(p.gross_value_eur ?? null);
        if (p.used_date == null) missing.push(`${p.bank_mnemonic ?? 'No bank'} · ${p.portfolio_number} has no positions on ${t.as_of}`);
        else if (g == null) missing.push(`${p.bank_mnemonic ?? 'No bank'} · ${p.portfolio_number}: its total has no exchange rate to EUR`);
        else grossEur += g;
      }
      const valueBy: Record<string, number> = {};
      const meta: Record<string, { name: string; authorized: boolean }> = {};
      let noIsin = 0;
      for (const r of t.rows) {
        if (r.is_cash || r.is_loan) continue;
        if (!r.security_id) { noIsin++; continue; }
        for (const [ref, line] of Object.entries(r.by_portfolio)) {
          const v = n(line.value_eur);
          if (v == null) { missing.push(`${r.name} in ${ref.split(':')[1] ?? ref}: no exchange rate to EUR`); continue; }
          valueBy[r.security_id] = (valueBy[r.security_id] ?? 0) + v;
        }
        meta[r.security_id] = { name: r.name, authorized: !!r.authorized };
      }
      if (missing.length || !grossEur) {
        notifications.show({ color: 'red', title: 'Rebalance not done', autoClose: false,
          message: `Consolidation ${cons.name} cannot be copied: ${missing.length ? missing.join('; ') : 'its total is zero'}.` });
        return;
      }
      const pctBy: Record<string, number> = {};
      for (const [sid, v] of Object.entries(valueBy)) if (v > 0) pctBy[sid] = (v / grossEur) * 100;
      await applyRebalance(`consolidation ${cons.name}`, 'consolidation', pctBy, meta, { date: t.as_of, noIsin });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Rebalance not done', message: errMsg(e), autoClose: false });
    } finally { setRebBusy(false); }
  };

  /** M-REB: Rebalance to a model — its effective weights (M3) as each security's Target %; its
   *  cash per currency reached with transfers (RQ2); the title defaults to the model (RQ5). */
  const rebalanceToModel = async (modelId: string, modelName: string) => {
    if (!single) return;
    setRebBusy(true);
    try {
      const w = await modelsApi.weights(modelId);
      const pctBy: Record<string, number> = {};
      const meta: Record<string, { name: string; authorized: boolean }> = {};
      const cashBy: Record<string, number> = {};
      for (const r of w.rows) {
        const p = Number(r.weight_pct);
        if (!(p > 0)) continue;
        if (r.kind === 'cash') { if (r.currency) cashBy[r.currency] = (cashBy[r.currency] ?? 0) + p; continue; }
        if (!r.security_id) continue;
        pctBy[r.security_id] = (pctBy[r.security_id] ?? 0) + p;
        // A member gone from the master cannot be bought either: treated as not approved.
        meta[r.security_id] = { name: r.name ?? r.isin ?? '?', authorized: !!r.approved };
      }
      const label = `model ${modelName}`;
      setTransfers([]);
      const ok = await applyRebalance(label, 'model', pctBy, meta, { date: null, noIsin: 0 });
      if (ok) {
        setPendingCash(cashBy);
        if (!title.trim()) setTitle(`Rebalance to model ${modelName}`);
      }
    } catch (e) {
      notifications.show({ color: 'red', title: 'Rebalance not done', message: errMsg(e), autoClose: false });
    } finally { setRebBusy(false); }
  };

  /** I1: does the row carry a trade (or is it a cash account a trade settles in)? */
  const isTraded = (r: ConsolidatedRow) => (r.is_cash ? !!cashMove[r.account_code ?? '']
    : r.key.startsWith(NEW_PREFIX) || (!!calc[r.key]?.side && (calc[r.key]?.q ?? 0) > 0));
  // N3 (Pedro 2026-10-25: "store on Save … Saving over the same draft replaces these documents"):
  // every save renders the PDF and Excel from the screen's figures and keeps them with the draft.
  // TF-S3 (Pedro 2026-10-25: "Save draft always produces documents, and is always available"):
  // saving an unchanged draft is allowed, and makes its documents again. They are opened from the
  // Trades list / panel only ("we separate the composition of the trade from the use of the trade
  // information"), so trade mode has no Export.
  const save = async (asNew: boolean) => {
    if (blockers.length) return;
    setSaving(true);
    let saved: TradeDraft | null = null;
    try {
      const d = draftId && !asNew ? await tradesApi.save(draftId, body(), override) : await tradesApi.create(body());
      setDraftId(d.id); setDirty(false); setLastSaved(d);
      saved = d;
      const docBody = buildDoc(d);
      if (docBody) {
        try {
          const withDocs = await tradesApi.storeDocuments(d.id, docBody, override);
          saved = { ...d, documents_at: withDocs.documents_at };
          setLastSaved(saved);
        } catch (e) {
          notifications.show({ color: 'orange', title: 'Draft saved; its PDF / Excel were not stored',
            message: `${errMsg(e)} Save again to store them.`, autoClose: false });
        }
      }
      await qc.invalidateQueries({ queryKey: ['trades', 'drafts', single] });
      void qc.invalidateQueries({ queryKey: ['trades', 'list'] });   // TRADE-LIST tabs (C-204)
      notifications.show({ color: 'green', title: 'Draft saved',
        message: `${d.serial ? `${d.serial} · ` : ''}${d.title ?? 'Untitled draft'} · ${d.line_count} trade${d.line_count === 1 ? '' : 's'}. The portfolio is unchanged.` });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Draft not saved', message: errMsg(e), autoClose: false });
    } finally { setSaving(false); }
  };

  // ── TRADE-DOC (Pedro 2026-10-23): the trade document, PDF or Excel — always a saved draft; no
  // reasons or marks on not-approved holdings. The figures are the screen's; the server checks
  // the orders are the saved lines. Stored on every save (N3), opened from the Trades lists. ──
  /** The document body of a saved draft from the screen's figures (null: nothing to send). */
  const buildDoc = (d: TradeDraft): TradeDocIn | null => {
    if (!single) return null;
    {
      const s = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? null : clean(v));
      // The screen's figures are in the currency shown (refCcy); the document's are in the
      // portfolio's reference currency and, per order, the security's (custodian's rates).
      const rates = ratesFor(single, ctx);
      const toRef = (v: number | null | undefined) => (v == null ? null : convert(v, refCcy, portfolioCcy, rates));
      const rowOf = (sid: string | null | undefined) => rows.find((r) => !r.is_cash && !r.is_loan && !!sid && r.security_id === sid);
      // TP-XFER: the saved transfers, after the orders ("Transfer out" / "Transfer in" in the document).
      const ccyOf = (code: string | null | undefined) => cash.find((a) => a.code === code)?.ccy ?? null;
      const transferOrders: TradeDocOrder[] = (d.lines ?? []).filter((l) => l.side === 'transfer').map((l) => ({
        security_id: null, side: 'transfer', name: null, isin: null, bb_ticker: null, currency: ccyOf(l.settle_account),
        quantity: l.quantity ?? '0', price: null, value_sec: null, accrued_sec: null,
        value_ref: l.amount_entered ?? null, settle_account: l.settle_account ?? null,
        to_account: l.to_account ?? null, to_amount: l.to_amount ?? null, to_currency: ccyOf(l.to_account) }));
      const orders: TradeDocOrder[] = (d.lines ?? []).filter((l) => l.side === 'buy' || l.side === 'sell').map((l) => {
        const r = rowOf(l.security_id);
        const k = r ? calc[r.key] : undefined;
        const q = n(l.quantity);
        const secCcy = l.currency ?? r?.currency ?? null;
        // The saved quantity × the line's value per unit (accrued interest included, ACC-1).
        const shown = k?.unitValue != null && q != null ? q * k.unitValue : null;
        const accruedRef = k?.accruedPerUnit != null && q != null ? q * k.accruedPerUnit : null;   // portfolio's ccy
        return { security_id: l.security_id ?? null, side: l.side as Side, name: l.name ?? r?.name ?? null,
          isin: l.isin ?? r?.isin ?? null, bb_ticker: r?.bb_ticker ?? null, currency: secCcy,
          quantity: l.quantity ?? '0', price: l.price ?? null, settle_account: l.settle_account ?? null,
          value_sec: s(shown != null && secCcy ? convert(shown, refCcy, secCcy, rates) : null),
          accrued_sec: s(accruedRef != null && secCcy ? convert(accruedRef, portfolioCcy, secCcy, rates) : 0),
          value_ref: s(toRef(shown)) };
      });
      const g0 = toRef(gross), g1 = toRef(newGross);
      // TP-PDF (Pedro 2026-10-24): "In Before and After, Cash lines need to come at the end", and
      // "Group and subtotals follow the view in force at the moment the Trade is exported": the
      // lines in the screen's grouping order (cash is its own last group), each with its path.
      const g = groupRows(rows.filter((r) => !r.is_loan), groupBy.levels, groupBy.lenses, CONSOLIDATED_ACCESS);
      const ordered = flattenGroups(g.groups, g.ungrouped);
      const pathOf = new Map(ordered.map((x) => [x.row.key, x.path]));
      const summary: TradeDocLine[] = ordered.map((x) => x.row).map((r) => {
        const k = calc[r.key];
        const v0 = toRef(r.key.startsWith(NEW_PREFIX) ? 0 : measure(r, single, 'value_ref', ctx));
        const v1 = toRef(newValue(r));
        const dq = k?.side && k.q > 0 ? (k.side === 'buy' ? k.q : -k.q) : 0;
        return { security_id: r.security_id ?? null, is_cash: !!r.is_cash, groups: pathOf.get(r.key) ?? [],
          name: r.name, isin: r.isin ?? null,
          qty_before: r.is_cash || !k ? null : s(k.held), value_before: s(v0),
          pct_before: s(v0 != null && g0 ? (v0 / g0) * 100 : null),
          qty_after: r.is_cash || !k ? null : s(k.held + dq), value_after: s(v1),
          pct_after: s(v1 != null && g1 ? (v1 / g1) * 100 : null) };
      });
      const buy = footSums('buy'), sell = footSums('sell'), net = footSums('all');
      const all = [...orders, ...transferOrders];
      if (!all.length) return null;
      return { format: 'pdf', orders: all, summary,
        buy_total: s(toRef(buy.any ? buy.value : 0)), sell_total: s(toRef(sell.any ? sell.value : 0)),
        net_total: s(toRef(net.any ? net.value : 0)), gross_before: s(g0), gross_after: s(g1) };
    }
  };
  const remove = async () => {
    if (!draftId) return;
    try {
      const r = await tradesApi.remove(draftId, override);
      setConfirmDelete(false);
      reset();
      await qc.invalidateQueries({ queryKey: ['trades', 'drafts', single] });
      void qc.invalidateQueries({ queryKey: ['trades', 'list'] });   // TRADE-LIST tabs (C-204)
      const moved = r.moved.map((m) => `${m.title}${m.problems.length ? ` (${m.problems.length} to fix)` : ''}`).join(', ');
      notifications.show({ color: 'green', title: 'Draft deleted',
        message: moved ? `Moved onto its starting point: ${moved}.` : 'The portfolio is unchanged.' });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Draft not deleted', message: errMsg(e), autoClose: false });
    }
  };

  // ── columns ──────────────────────────────────────────────────────────────
  const R = `Ref Ccy ${refCcy}`;
  // TRADE-UX (Pedro 2026-10-23): narrow by default (headings wrap), resizable and draggable among
  // themselves, the layout remembered on this computer like every grid's.
  const baseTcols: TCol[] = active && single ? [
    { key: 'buy', label: 'Buy', width: 50, tip: 'Tick to buy more of this security (Approved securities only).' },
    { key: 'sell', label: 'Sell', width: 50, tip: 'Tick to sell (never more than is held).' },
    { key: 'amount', label: target ? 'Target % after trade' : UNIT_LABEL(unit, refCcy), width: 110, num: true,
      tip: `How much to buy or sell, in ${UNIT_LABEL(unit, refCcy)}. The quantity is then checked against the security's minimum lot and increment and adjusted (an orange mark says how).` },
    { key: 'price', label: 'Trade price (Sec Ccy)', width: 96, num: true,
      tip: "The price the trade is valued at, in the security's currency (a bond's per 100 nominal): the portfolio's own line for a security it holds; for a new one, the first price found — another portfolio of the same custodian that day, another custodian that day, the Securities price that day, the same custodian's last price — or one you type. Every price not the portfolio's own is shaded yellow and says where it came from." },
    { key: 'accrued_unit', label: 'Accrued interest / unit', width: 120, num: true,
      tip: "Accrued interest per unit of quantity, added to the trade's value (a bond is bought and sold with its accrued interest). For a new bond it is borrowed from the line its price came from, or typed; it will later be calculated from the bond's data." },
    ...cash.map((a) => ({ key: `cash:${a.code}`, label: `${a.code} (${a.ccy})`, width: 110,
      tip: `Settle in cash account ${a.code}, in ${a.ccy}. The standard choice above pre-fills every trade; change any line here.` })),
    { key: 'keep', label: 'Reason to keep (not approved)', width: 170,
      tip: 'A security not approved in Securities may only be sold. Keeping it (all or part) needs a reason, saved with the draft.' },
    { key: 'traded', label: `Traded (${unit === 'quantity' ? 'Quantity' : unit === 'value' ? R : '%'})`, width: 100, num: true, after: true,
      tip: `This draft's trade, in ${UNIT_LABEL(unit, refCcy)}: purchases positive, sales negative, after the lot adjustment.` },
    // Pedro 2026-10-23: "we need a Traded Quantity column" (whatever the unit).
    { key: 'traded_qty', label: 'Traded Quantity', width: 100, num: true, after: true,
      tip: "This draft's trade in quantity (a bond's in nominal): purchases positive, sales negative, after the lot adjustment." },
    { key: 'traded_value', label: `Traded value (${R})`, width: 110, num: true, after: true,
      tip: `The trade's value in ${refCcy}, accrued interest included (as View portfolio's Value): purchases positive, sales negative.` },
    { key: 'new_qty', label: 'New Quantity', width: 100, num: true, after: true, tip: 'The quantity after this draft.' },
    { key: 'new_value', label: `New Value (${R})`, width: 110, num: true, after: true,
      tip: `The value after this draft, in ${refCcy}, accrued interest included; a cash account's value after the trades settled in it.` },
    { key: 'new_pct', label: 'New % of portfolio', width: 84, num: true, after: true,
      tip: 'The share of the gross value after this draft (loans left out).' },
  ] : [];
  const tOrder = useColumnOrder('trades.columnOrder', baseTcols.map((x) => x.key));
  const tWidths = useColumnWidths('trades.columnWidths');
  const tDrag = useHeadingDrag(tOrder.place);
  const tcols: TCol[] = ordered(baseTcols, tOrder.order).map((x) => ({ ...x, width: tWidths.widthOf(x.key, x.width) }));
  const width = tcols.reduce((s, x) => s + x.width, 0);

  const cellStyle = (x: TCol, bg: string, bold = false): React.CSSProperties => ({
    width: x.width, minWidth: x.width, maxWidth: x.width, padding: '2px 6px', boxSizing: 'border-box', background: bg,
    borderBottom: '1px solid var(--mantine-color-gray-3)', borderRight: '1px solid var(--mantine-color-gray-2)',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13,
    textAlign: x.num ? 'right' : 'left', fontWeight: bold ? 600 : undefined,
  });
  // Colours (app-language §2): the entry columns are orange (staged, pending until the draft is
  // saved); the after-view columns are ordinary headings (blue would read "filed for approval").
  const headBg = (x: TCol) => (x.after ? 'var(--mantine-color-gray-1)' : 'var(--mantine-color-orange-1)');

  // Headings wrap onto as many lines as the width needs (TRADE-UX 3); drag to move, drag the
  // right edge to resize.
  const headCells = (rowSpan: number, top: number) => tcols.map((x) => (
    <th key={`t:${x.key}`} rowSpan={rowSpan} {...tDrag.props(x.key)}
      style={{ ...cellStyle(x, headBg(x), true), whiteSpace: 'normal', lineHeight: 1.2, position: 'sticky', top, zIndex: 3,
        verticalAlign: 'bottom', ...tDrag.marker(x.key) }}>
      <Tooltip label={`${x.tip} Drag to move the column; drag its right edge to resize.`} multiline w={300} withArrow openDelay={300}>
        <span style={{ display: 'block' }}>{x.label}</span>
      </Tooltip>
      <ColumnResizeHandle width={x.width} onResize={(w) => tWidths.setWidth(x.key, w)} />
    </th>
  ));

  const unitTraded = (k: { q: number; unitValue: number | null }, side: Side) => {
    const sign = side === 'buy' ? 1 : -1;
    const v = fromQuantity(k.q, target ? 'pct' : unit, k.unitValue, gross);
    return v == null ? null : sign * v;
  };

  const rowCells = (row: ConsolidatedRow, bg: string): React.ReactNode[] => tcols.map((x) => {
    const k = calc[row.key];
    const e = entries[row.key] ?? EMPTY;
    const st = cellStyle(x, bg);
    const blank = <td key={`t:${x.key}`} style={st} />;
    if (row.is_loan) return blank;
    if (row.is_cash) {
      // TF-XFER3 (Pedro 2026-10-25): a cash row has a Sell tick only — cash LEAVES this account; it
      // opens the transfer window with this account as From. A receiving account shows a
      // read-only "in" mark in its Buy cell (4a), naming the sender. Orange while it differs from
      // the saved draft.
      if (x.key === 'buy') {
        const code = row.account_code ?? '';
        const senders = transfers.filter((t) => t.to === code);
        if (!senders.length) return blank;
        return (
          <td key={`t:${x.key}`} style={{ ...st, textAlign: 'center' }}
            title={`Receives cash from ${senders.map((t) => t.from).join(', ')}: change it from that account's Sell tick.`}>
            <Text span size="xs" c="dimmed">in</Text>
          </td>);
      }
      if (x.key === 'sell') {
        const code = row.account_code ?? '';
        const on = transfers.some((t) => t.from === code);
        const changed = xferSig(code, transfers) !== xferSig(code, savedTransfers);
        const why = cash.length < 2 ? 'The portfolio has only one cash account.' : null;
        const tip = why ?? (on ? `${code} sends cash to another account: click to change or remove it`
          : `Transfer cash from ${code} to another cash account`);
        return (
          <td key={`t:${x.key}`} title={tip}
            style={{ ...st, textAlign: 'center', background: on && changed ? 'var(--mantine-color-orange-1)' : st.background }}>
            <Checkbox size="xs" checked={on} disabled={!!why} aria-label={`Transfer from ${code}`}
              onChange={() => openTransfers(code)} styles={{ body: { justifyContent: 'center' } }} />
          </td>);
      }
      if (x.key === 'new_value') {
        // A cash account the trades would overdraw is marked red, with the reason.
        const v = newValue(row);
        const neg = v != null && v < -0.005 && (cashMove[row.account_code ?? ''] ?? 0) < 0;
        return (
          <td key={`t:${x.key}`} style={neg ? { ...st, background: 'var(--mantine-color-red-1)', color: 'var(--mantine-color-red-8)', fontWeight: 600 } : st}
            title={neg ? `The trades settled in ${row.account_code} would leave it negative.` : undefined}>{amount(v)}</td>);
      }
      if (x.key === 'new_pct') { const v = newValue(row); return <td key={`t:${x.key}`} style={st}>{pctFmt(v != null && newGross ? (v / newGross) * 100 : null)}</td>; }
      if (x.key === 'traded_value') { const m = cashMove[row.account_code ?? '']; return <td key={`t:${x.key}`} style={st} title={m ? 'Cash paid (−) or received (+) by the trades settled in this account' : undefined}>{m ? amount(m) : ''}</td>; }
      if (x.key === 'traded') {
        // I2 (Pedro: "We could use a Traded % value in each cash row"): the cash moved, in the unit
        // (Quantity = the amount in the account's own currency).
        const m = cashMove[row.account_code ?? ''];
        if (!m) return blank;
        const v = unit === 'pct' || target ? (gross ? (m / gross) * 100 : null)
          : unit === 'value' ? m : convert(m, refCcy, row.currency, ratesFor(single!, ctx));
        return <td key={`t:${x.key}`} style={st}>{unit === 'pct' || target ? pctFmt(v) : amount(v)}</td>;
      }
      return blank;
    }
    if (!k) return blank;
    const traded = !!k.side && k.q > 0;
    const isNew = row.key.startsWith(NEW_PREFIX);
    const a = isNew ? added.find((y) => `${NEW_PREFIX}${y.sec.id}` === row.key) : undefined;
    const inputStyle: React.CSSProperties = { width: '100%', textAlign: 'right', font: 'inherit',
      border: '1px solid var(--mantine-color-gray-4)', borderRadius: 4, padding: '1px 4px' };
    switch (x.key) {
      case 'price': {
        if (!a) return <td key={`t:${x.key}`} style={st} title={SOURCE_TEXT.own}>{fmt(k.price, 2, 6)}</td>;
        // TQ3: every price not the portfolio's own is loudly flagged, with its source.
        const why = a.source == null ? 'No price found in the app: type one.'
          : `Price from ${SOURCE_TEXT[a.source]}${a.priceDate ? ` (${a.priceDate})` : ''}.${a.factorGuessed && a.factor < 1 ? ' Taken as per 100 nominal (a bond).' : ''}`;
        return (
          <td key={`t:${x.key}`} title={why}
            style={{ ...st, background: n(a.price) ? 'var(--mantine-color-yellow-2)' : 'var(--mantine-color-red-1)' }}>
            <input type="number" min={0} step="any" value={a.price} aria-label={`Price for ${row.name}`}
              onChange={(ev) => editAdded(row.key, { price: ev.currentTarget.value, source: 'typed', priceDate: c.as_of })}
              style={inputStyle} />
          </td>);
      }
      case 'accrued_unit': {
        if (!a) return <td key={`t:${x.key}`} style={st}>{k.accruedPerUnit ? `${fmt(k.accruedPerUnit, 2, 6)} ${portfolioCcy}` : ''}</td>;
        const missing = a.factor < 1 && a.accrued.trim() === '';
        return (
          <td key={`t:${x.key}`} title={missing ? 'A bond is traded with its accrued interest: type it per unit (0 if none).' : `In ${a.accruedCcy}.`}
            style={{ ...st, background: missing ? 'var(--mantine-color-red-1)' : a.accrued.trim() ? 'var(--mantine-color-yellow-2)' : st.background }}>
            <Group gap={4} wrap="nowrap">
              <input type="number" step="any" value={a.accrued} aria-label={`Accrued interest per unit for ${row.name}`}
                onChange={(ev) => editAdded(row.key, { accrued: ev.currentTarget.value })} style={inputStyle} />
              <Text size="xs" c="dimmed">{a.accruedCcy}</Text>
            </Group>
          </td>);
      }
      case 'buy': {
        if (isNew) {
          return (
            <td key={`t:${x.key}`} style={{ ...st, textAlign: 'center' }} title="New security, bought. Untick to remove it from the draft.">
              <Checkbox size="xs" checked aria-label={`Remove ${row.name}`} onChange={() => removeAdded(row.key)}
                styles={{ body: { justifyContent: 'center' } }} />
            </td>);
        }
        const why = target ? 'Target %: buy or sell is worked out from the target.'
          : !row.authorized ? 'Not approved in Securities: it can only be sold.' : !row.security_id ? 'Not in Securities.' : null;
        return (
          <td key={`t:${x.key}`} style={{ ...st, textAlign: 'center' }} title={why ?? undefined}>
            <Checkbox size="xs" checked={k.side === 'buy'} disabled={!!why} aria-label={`Buy ${row.name}`}
              onChange={() => setSide(row, 'buy')} styles={{ body: { justifyContent: 'center' } }} />
          </td>);
      }
      case 'sell':
        return (
          <td key={`t:${x.key}`} style={{ ...st, textAlign: 'center' }}
            title={target ? 'Target %: buy or sell is worked out from the target.' : !row.security_id ? 'Not in Securities: not tradable here yet.'
              : k.held <= 0 ? 'Not held in this portfolio: nothing to sell.' : undefined}>
            <Checkbox size="xs" checked={k.side === 'sell'} disabled={target || !row.security_id || k.held <= 0} aria-label={`Sell ${row.name}`}
              onChange={() => setSide(row, 'sell')} styles={{ body: { justifyContent: 'center' } }} />
          </td>);
      case 'amount':
        return (
          <td key={`t:${x.key}`} style={{ ...st, background: k.note ? 'var(--mantine-color-orange-1)' : st.background }}
            title={k.note ? `Adjusted: ${k.note}. Quantity traded: ${qtyFmt(k.q)}.` : undefined}>
            <Group gap={4} wrap="nowrap" justify="flex-end">
              {k.note && <IconInfoCircle size={13} color="var(--mantine-color-orange-7)" />}
              <input type="number" min={0} step="any" value={e.amount} disabled={!target && !e.side}
                aria-label={`Amount for ${row.name}`}
                onChange={(ev) => setEntry(row.key, { amount: ev.currentTarget.value,
                  ...(target && !e.settle ? { settle: defaultAccount(row) } : {}) })}
                onFocus={() => { focusAmount[row.key] = e.amount; }}
                onBlur={() => {
                  if (target) {
                    const want = n(e.amount);
                    // TF-CAP100: no target above 100% of the portfolio.
                    if (want != null && want > 100) {
                      setEntry(row.key, { amount: '100' });
                      notifications.show({ color: 'red', autoClose: 10000, title: 'Target capped',
                        message: `${row.name}: a target can be at most 100% of the portfolio. ${pctFmt(want)} was cut to 100.00%.` });
                      return;
                    }
                    // Pedro 2026-10-23: a not-approved security's target may only be equal to or
                    // smaller than its weight now; anything else gets a loud warning and is rejected.
                    const cur = notApprovedWeight(row);
                    if (cur == null || want == null || want <= cur) return;
                    if (want <= cur + 0.005) { setEntry(row.key, { amount: clean(cur) }); return; }   // its weight as shown (2 decimals)
                    setEntry(row.key, { amount: focusAmount[row.key] ?? '' });
                    // A rejected entry, already put back: loud but passing (Pedro's smoke: "pop does
                    // not go away") — app-language §9.
                    notifications.show({ color: 'red', autoClose: 10000, title: 'Target rejected',
                      message: `${row.name} is not Approved in Securities: its target can only be equal to or smaller than its weight now (${pctFmt(cur)}). ${pctFmt(want)} was not accepted.` });
                    return;
                  }
                  // a target is what was asked; the trade shows the adjustment
                  // Pedro 2026-10-23: on leaving the field, an amount the app adjusted is rewritten
                  // to the adjusted figure, in the unit: a sale above the holding "automatically
                  // revert[s] to the maximum allowed"; an amount adjusted to the minimum lot or
                  // increment "is also rewritten to the adjusted figure".
                  if (!e.side || !k.note) return;
                  const adjusted = fromQuantity(k.q, unit, k.unitValue, gross);
                  if (adjusted != null) setEntry(row.key, { amount: clean(adjusted) });
                  // TF-CAP100: a buy cut to 100% of the portfolio is said loudly (app-language §9).
                  if (k.capped) {
                    notifications.show({ color: 'red', autoClose: 10000, title: 'Buy capped',
                      message: `${row.name}: a buy can take a security to at most 100% of the portfolio. The amount was cut${adjusted != null ? ` to ${unit === 'pct' ? pctFmt(adjusted) : clean(adjusted)}` : ''}.` });
                  } else if (adjusted != null && Math.abs(adjusted - Number(e.amount)) > 1e-9) {
                    // TRADE-CONS 4b smoke (2026-10-30): any rewrite is said, with why (as in a consolidation).
                    const f = unit === 'pct' ? pctFmt : unit === 'quantity' ? qtyFmt : amount;
                    notifications.show({ color: 'orange', autoClose: 10000, title: 'Amount adjusted',
                      message: `${row.name}: ${f(Number(e.amount))} became ${f(adjusted)} — ${k.note}.` });
                  }
                }}
                style={{ width: '100%', minWidth: 40, textAlign: 'right', font: 'inherit', border: '1px solid var(--mantine-color-gray-4)', borderRadius: 4, padding: '1px 4px' }} />
            </Group>
          </td>);
      case 'keep':
        if (!k.notApprovedKept) return blank;
        return (
          <td key={`t:${x.key}`} style={{ ...st, background: (e.note ?? '').trim() ? 'var(--mantine-color-yellow-1)' : 'var(--mantine-color-yellow-3)' }}
            title="Not approved: kept (in full or in part). Give the reason, e.g. tax, lock-up, client instruction.">
            <input type="text" value={e.note} placeholder="Reason (required)" aria-label={`Reason to keep ${row.name}`}
              onChange={(ev) => setEntry(row.key, { note: ev.currentTarget.value })}
              style={{ width: '100%', font: 'inherit', border: '1px solid var(--mantine-color-yellow-7)', borderRadius: 4, padding: '1px 4px' }} />
          </td>);
      case 'traded': return <td key={`t:${x.key}`} style={st}>{traded ? (unit === 'quantity' && !target ? qtyFmt : unit === 'pct' || target ? pctFmt : amount)(unitTraded(k, k.side!)) : ''}</td>;
      case 'traded_qty': return <td key={`t:${x.key}`} style={st}>{traded ? qtyFmt(k.side === 'buy' ? k.q : -k.q) : ''}</td>;
      case 'traded_value': return <td key={`t:${x.key}`} style={st}>{traded ? amount(k.side === 'buy' ? k.value : -k.value) : ''}</td>;
      case 'new_qty': return <td key={`t:${x.key}`} style={st}>{qtyFmt(k.held + (traded ? (k.side === 'buy' ? k.q : -k.q) : 0))}</td>;
      case 'new_value': return <td key={`t:${x.key}`} style={st}>{amount(newValue(row))}</td>;
      case 'new_pct': { const v = newValue(row); return <td key={`t:${x.key}`} style={st}>{pctFmt(v != null && newGross ? (v / newGross) * 100 : null)}</td>; }
      default:
        if (x.key.startsWith('cash:')) {
          const code = x.key.slice(5);
          return (
            <td key={`t:${x.key}`} style={{ ...st, textAlign: 'center' }}>
              {k.side && <Radio size="xs" checked={e.settle === code} aria-label={`Settle ${row.name} in ${code}`}
                onChange={() => setEntry(row.key, { settle: code })} styles={{ body: { justifyContent: 'center' } }} />}
            </td>);
        }
        return blank;
    }
  });

  // Cells of the sticky total rows stay with them (TRADE-FOOT, Pedro's smoke 2026-10-23: the
  // total rows showed no values once the table scrolled): `bottom` = the row's sticky offset.
  const sticky = (bottom?: number): React.CSSProperties =>
    (bottom == null ? {} : { position: 'sticky', bottom, zIndex: 1 });
  const blankCells = (bg: string, bold = false, bottom?: number) =>
    tcols.map((x) => <td key={`t:${x.key}`} style={{ ...cellStyle(x, bg, bold), ...sticky(bottom) }} />);
  // TRADE-UX 1 (Pedro 2026-10-23): Buy total = the securities bought, Sell total = those sold —
  // their Traded (in Value / % only: quantities of different securities do not add up), Traded
  // value and New Value; Net total = the whole portfolio after the trades, whose New % "should
  // always naturally equal 100.00%" (Pedro: no New % on the Buy / Sell rows).
  const footSums = (which: Side | 'all') => {
    let traded = 0, value = 0, newV = 0, any = false;
    for (const r of rows) {
      const k = calc[r.key];
      if (!k || !k.side || k.q <= 0 || (which !== 'all' && k.side !== which)) continue;
      any = true;
      traded += unitTraded(k, k.side) ?? 0;
      value += k.side === 'buy' ? k.value : -k.value;
      newV += newValue(r) ?? 0;
    }
    if (which === 'all') {
      // Every line but the loans, after the trades: the new gross, 100 % of itself.
      newV = 0;
      let complete = true;
      for (const r of rows) { if (r.is_loan) continue; const v = newValue(r); if (v == null) complete = false; else newV += v; }
      return { any, traded, value, newV: complete ? newV : null, newPct: complete && newGross ? (newV / newGross) * 100 : null };
    }
    return { any, traded, value, newV: any ? newV : null, newPct: null };
  };
  const pctUnit = unit === 'pct' || target;
  const foot = (label: string, tip: string, which: Side | 'all'): { label: string; tip: string; cells: (bottom: number) => React.ReactNode[] } => {
    const s = footSums(which);
    return {
      label, tip,
      cells: (bottom) => tcols.map((x) => {
        let v: React.ReactNode = '';
        if (x.key === 'traded_value') v = amount(s.any ? s.value : 0);
        else if (s.any && x.key === 'traded' && (pctUnit || unit === 'value')) v = pctUnit ? pctFmt(s.traded) : amount(s.traded);
        else if (x.key === 'new_value' && s.newV != null) v = amount(s.newV);
        else if (x.key === 'new_pct' && which === 'all') v = pctFmt(s.newPct);
        return <td key={`t:${x.key}`} style={{ ...cellStyle(x, 'var(--mantine-color-gray-2)', true), ...sticky(bottom) }}>{v}</td>;
      }),
    };
  };
  const footRows = active && single ? [
    foot('Buy total', `The securities bought in this draft, in ${refCcy}: what they cost, and their value after it.`, 'buy'),
    foot('Sell total', `The securities sold in this draft, in ${refCcy} (negative: cash coming in), and their value after it.`, 'sell'),
    foot('Net total', `Purchases less sales, in ${refCcy} (what the cash accounts pay out net); New Value and New % are the whole portfolio after the trades (loans left out), so New % is 100.00%.`, 'all'),
  ] : [];

  // ── toolbar ──────────────────────────────────────────────────────────────
  const canTrade = !!single && enabled;
  const button = !enabled ? null : active ? (
    <Button size="xs" variant="default" onClick={() => void leave()}>Leave trade mode</Button>
  ) : (
    <Tooltip withArrow multiline w={340} label={single ? 'Define trades on this portfolio: Buy / Sell columns, settlement accounts, drafts' : notSingleWhy}>
      <span>
        <Button size="xs" leftSection={<IconArrowsExchange size={14} />} disabled={!canTrade} onClick={enter}>Trade</Button>
      </span>
    </Tooltip>
  );

  const draftOptions = [{ value: '__new__', label: 'New draft' },
    ...(draftsQ.data?.items ?? []).map((d) => ({ value: d.id,
      // TRADES-RM: drafts are shared per portfolio, so each names who saved it and when;
      // TRADE-LIST: led by its serial, and marked when sent for execution.
      // TRADE-CONS 4b: a run's draft is listed with its run (it opens read-only here).
      label: `${d.serial ? `${d.serial} · ` : ''}${d.run_serial ? `run ${d.run_serial} · ` : ''}${d.title ?? 'Untitled'} · ${d.line_count} trade${d.line_count === 1 ? '' : 's'} ${d.updated_by_name ? ` · ${d.updated_by_name}` : ''} · ${new Date(d.updated_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}${d.locked ? ` · ${d.status_label ?? 'locked'}` : ''}` }))];
  // O8 (TRADE-CONS): a run's draft changes only through its run — read-only here, override or not.
  const inRun = !!draftId && !!lastSaved?.run_id;
  const locked = inRun || (!!draftId && !!lastSaved?.locked && !override);
  const panel = !active ? null : (
    <Stack gap={4}>
      <Group gap="xs" align="flex-end" wrap="wrap"
        style={{ background: 'var(--mantine-color-orange-0)', border: '1px solid var(--mantine-color-orange-3)', borderRadius: 6, padding: '6px 8px' }}>
        <Text size="sm" fw={700} c="orange.8">Trade mode</Text>
        <Select size="xs" label="Draft" w={260} allowDeselect={false} data={draftOptions}
          value={draftId ?? '__new__'} onChange={(v) => void loadDraft(v === '__new__' ? null : v)} />
        <TextInput size="xs" label="Title" w={200} placeholder="e.g. Rebalance November" value={title}
          onChange={(e) => { setTitle(e.currentTarget.value); setDirty(true); }} />
        <Tooltip withArrow disabled={!target} label="Target % is on: turn it off to type in a unit again.">
          <Stack gap={2}>
            <Text size="xs" fw={500}>Unit</Text>
            <SegmentedControl size="xs" value={unit} onChange={(v) => changeUnit(v as Unit)} disabled={target}
              data={[{ value: 'quantity', label: 'Quantity' }, { value: 'value', label: `Value (${R})` }, { value: 'pct', label: '% of portfolio' }]} />
          </Stack>
        </Tooltip>
        {/* I6 (Pedro 2026-10-23): "a new group of similar buttons. There will be more". */}
        <Stack gap={2}>
          <Text size="xs" fw={500}>Work out the trades from</Text>
          <Group gap={4}>
            <Tooltip withArrow multiline w={300} label="Type, for each security, the % of the portfolio you want it to be after the trade: the app buys or sells the difference (then the lot rules apply). Click again to go back to typing amounts.">
              <Button size="xs" variant={target ? 'filled' : 'default'} onClick={() => toggleTarget(!target)}>Target %</Button>
            </Tooltip>
            {/* M-REB (Pedro 2026-10-26): "Rebalance to... can open a choice with buttons: Model and
                Portfolio, which then open either a Model picker or a Portfolio picker". */}
            <Menu shadow="md" position="bottom-start" withArrow>
              <Menu.Target>
                <Tooltip withArrow multiline w={320} label="Work out the trades that bring this portfolio to a model, another portfolio or a consolidation. Refused as a whole if the target holds more of a not-approved security than this portfolio.">
                  <Button size="xs" variant="default" loading={rebBusy}>Rebalance to…</Button>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Rebalance to</Menu.Label>
                <Menu.Item onClick={() => setRebPick('model')}>Model…</Menu.Item>
                <Menu.Item onClick={() => setRebPick('portfolio')}>Portfolio…</Menu.Item>
                <Menu.Item onClick={() => setRebPick('consolidation')}>Consolidation…</Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <ConsolidationPicker opened={rebPick === 'consolidation'} onClose={() => setRebPick(null)} busy={rebBusy} action="Rebalance"
              onChoose={(cons) => void rebalanceToConsolidation(cons)} target={<span />}
              intro="Choose the consolidation to copy: each security's Target % becomes its weight in the consolidation (its total across the consolidation's portfolios, as % of their combined value); 0 for the ones it does not hold, which are sold. Cash is what is left. The trades on screen are replaced." />
            <ModelPicker opened={rebPick === 'model'} onClose={() => setRebPick(null)} busy={rebBusy} action="Rebalance"
              defaultId={rebDefaultId} defaultNote={rebDefaultNote}
              onChoose={(id, name) => void rebalanceToModel(id, name)} target={<span />}
              intro="Choose the model: each security's Target % becomes its weight in the model (0 for the ones it does not hold, which are sold), and the model's cash per currency is reached with cash transfers. The trades on screen are replaced." />
            {/* Pedro 2026-10-23: the same picker as View portfolio's, choosing one portfolio. */}
            <PortfolioPicker value={[]} onChange={(v) => void rebalanceTo(v[0] ?? null)}
              single={{ button: 'Rebalance to…', action: 'Rebalance', exclude: single, busy: rebBusy,
                opened: rebPick === 'portfolio', onClose: () => setRebPick(null), target: <span />,
                intro: "Choose the portfolio to copy: this portfolio's trades are worked out to reach its positions (as % of the portfolio). Cash is what is left. The trades on screen are replaced." }} />
          </Group>
        </Stack>
        <Tooltip withArrow multiline w={300} label="Pre-fills the settlement account of every trade; each line can then be changed in its cash-account column.">
          <Select size="xs" label="Settle in" w={190} allowDeselect={false}
            data={[{ value: 'ref', label: `Reference currency (${portfolioCcy})` }, { value: 'sec', label: 'Security currency' },
              ...cash.map((a) => ({ value: `other:${a.code}`, label: `Other: ${a.code} (${a.ccy})` }))]}
            value={std === 'other' ? `other:${stdOther}` : std}
            onChange={(v) => { if (!v) return; if (v.startsWith('other:')) applyStd('other', v.slice(6)); else applyStd(v as SettleDefault, null); }} />
        </Tooltip>
        {/* I1: show only the rows that carry a trade (and the cash accounts they settle in). */}
        <Checkbox size="xs" label="Traded only" checked={onlyTraded} onChange={(ev) => setOnlyTraded(ev.currentTarget.checked)} mb={6} />
        {/* TF-S3: always available (also unchanged: it makes the PDF / Excel again), except a
            trade that is no longer a Draft (without the Admin override) or with problems to fix. */}
        <Tooltip withArrow multiline w={300} disabled={!locked && blockers.length === 0}
          label={inRun ? `Part of run ${lastSaved?.run_serial}: it is changed only through the run (show its consolidation and trade it there). Save as a new draft to start a trade of this portfolio alone from it.`
            : locked ? 'No longer a draft: it cannot be changed. Save as a new draft to start a new trade from it.'
            : 'Fix what is listed below first.'}>
          <span>
            <Button size="xs" onClick={() => askSave(false)} loading={saving} disabled={locked || blockers.length > 0}
              title={!locked && blockers.length === 0 ? 'Saves the draft and makes its trade orders (PDF and Excel), opened from Trades' : undefined}>
              {draftId ? 'Save draft' : 'Save as draft'}
            </Button>
          </span>
        </Tooltip>
        {draftId && (
          <Tooltip withArrow disabled={blockers.length === 0} label="Fix what is listed below first.">
            <span><Button size="xs" variant="light" onClick={() => askSave(true)} loading={saving} disabled={blockers.length > 0}>Save as a new draft</Button></span>
          </Tooltip>
        )}
        {/* I4 */}
        {(() => {
          const none = !Object.values(calc).some((k) => k.side && k.q > 0) && added.length === 0 && transfers.length === 0;
          return (
            <Tooltip withArrow disabled={!none} label="No trades on screen to clear.">
              <span><Button size="xs" variant="default" onClick={() => setConfirmClear(true)} disabled={none}>Clear all trades</Button></span>
            </Tooltip>
          );
        })()}
        {draftId && (
          <Tooltip withArrow disabled={!locked} label={inRun ? `Part of run ${lastSaved?.run_serial}: delete the run instead (its consolidation's trade mode).`
            : 'No longer a draft: it cannot be deleted. Cancel it from Trades instead.'}>
            <span><Button size="xs" variant="light" color="red" disabled={locked} onClick={() => setConfirmDelete(true)}>Delete draft</Button></span>
          </Tooltip>
        )}
        <AdminOverrideSwitch />
        <Text size="xs" c="dimmed" ml="auto">Drafts change nothing in the portfolio; only Upload positions does.</Text>
      </Group>
      {/* TF-OPP-NOTE (Pedro's smoke: "do not see a yellow note"): opposite transfers are also noted
          here, not only inside the transfer window. */}
      {transfers.filter((t) => t.from < t.to && transfers.some((u) => u.from === t.to && u.to === t.from)).map((t) => (
        <Alert key={`${t.from}>${t.to}`} color="yellow" py={4}>
          <Text size="xs">{t.from} and {t.to} send cash to each other: two separate transfers (two FX trades). To move less, reduce one of them instead (its account's Sell tick).</Text>
        </Alert>
      ))}
      {!single && (
        <Alert color="orange" py={4}>{notSingleWhy}</Alert>
      )}
      {single && !isLatest && (
        <Alert color="yellow" py={4}>Trades are always set on the latest positions ({latest}); this view shows {c.as_of}.{' '}
          <Button size="compact-xs" variant="light" onClick={onLatest}>Show {latest}</Button>
        </Alert>
      )}
      {inRun && (
        <Alert color="gray" py={4}>
          {lastSaved?.serial} is this portfolio's part of run {lastSaved?.run_serial} (a consolidation traded as one): it is shown
          read-only and changed only through the run. Save as a new draft starts a trade of this portfolio alone from it.{' '}
          {/* Smoke 2026-10-30 (Pedro: "The grey banner could have a link to the TRC"). */}
          {onOpenRun && lastSaved?.run_id && (
            <Button size="compact-xs" variant="light" onClick={() => { const id = lastSaved.run_id!; reset(); setActive(false); onOpenRun(id); }}>
              Open run {lastSaved.run_serial}
            </Button>
          )}
        </Alert>
      )}
      {draftId && lastSaved?.locked && !inRun && (
        <Alert color="gray" py={4}>
          {lastSaved.serial} is {lastSaved.status_label ?? 'no longer a draft'}: only a Draft can be changed{override
            ? ' — your Admin override (Beta) is on, so you can still change it (each change is logged)'
            : '. Save as a new draft to start a new trade from it; if the client rejected it, Reject → Revise in Trades does the same'}.
        </Alert>
      )}
      {blockers.length > 0 && (
        // M-REB-LOUD (Pedro 2026-10-26): red, as the warning about deleting a security in a model.
        <Alert color="red" variant="filled" py={4} icon={<IconAlertTriangle size={18} />}
          title={`To fix before saving: ${blockers.length} problem${blockers.length === 1 ? '' : 's'} (the rows concerned are red)`}>
          <Stack gap={0}>{blockers.map((b) => <Text key={b} size="xs">{b}</Text>)}</Stack>
        </Alert>
      )}
      {lastSaved?.newer_positions && (
        <Alert color="yellow" py={4}>Built on the positions of {lastSaved.as_of_date}; newer positions exist ({lastSaved.latest_positions_date}). It is shown over the latest positions.</Alert>
      )}
      {(lastSaved?.problems.length ?? 0) > 0 && (
        <Alert color="red" py={4} title="This draft no longer works over its starting point">
          <Stack gap={0}>{lastSaved!.problems.map((p) => <Text key={p} size="xs">{p}</Text>)}</Stack>
        </Alert>
      )}
      {(lastSaved?.dependents ?? []).filter((d) => d.problems.length).map((d) => (
        <Alert key={d.id} color="red" py={4} title={`Draft "${d.title}" is based on this one and no longer works`}>
          <Stack gap={0}>{d.problems.map((p) => <Text key={p} size="xs">{p}</Text>)}</Stack>
        </Alert>
      ))}
      {rebNote && (
        // M-REB-LOUD (Pedro 2026-10-26: "we need a louder warning"): red when something could not be done.
        <Alert color={notePlain ? 'gray' : 'red'} py={4} withCloseButton onClose={() => setRebNote(null)}
          title={rebNote.kind === 'model' ? `Rebalanced to ${rebNote.label}` : `Rebalanced to ${rebNote.label} (positions of ${rebNote.date})`}>
          <Stack gap={0}>
            <Text size="xs">{rebNote.kind === 'model'
              ? "Each security's Target % is its weight in the model; the model's cash per currency is reached with the cash transfers below. The lot rules apply, so the result is close to the target, not always exact."
              : rebNote.kind === 'consolidation'
                ? "Each security's Target % is its share of the consolidation (its total across the consolidation's portfolios, in EUR, as % of their combined value); cash is what is left. The lot rules apply, so the result is close to the target, not always exact."
                : "Each security's Target % is its share of that portfolio; cash is what is left. The lot rules apply, so the result is close to the target, not always exact."}</Text>
            {rebNote.noIsin > 0 && <Text size="xs" c="yellow.9">{rebNote.noIsin} line(s) of that {rebNote.kind === 'consolidation' ? 'consolidation' : 'portfolio'} without an ISIN were left out.</Text>}
            {/* Live (worked out from the table now): each currency's cash, model vs after these trades. */}
            {liveNote?.lines.map((l) => <Text key={l} size="xs">{l}</Text>)}
            {rebNote.cash.map((l) => <Text key={l} size="xs">{l}</Text>)}
            {/* Pedro 2026-10-26: "never silently skip anything" — each one named (live, then those
                fixed at Rebalance: a missing account or rate, not enough cash to spare). */}
            {liveNote?.problems.map((p) => <Text key={p} size="xs" c="red.8" fw={500}>{p}</Text>)}
            {rebNote.problems.map((p) => <Text key={p} size="xs" c="red.8" fw={500}>{p}</Text>)}
          </Stack>
        </Alert>
      )}
      {/* Pedro 2026-10-23: the whole rebalance is rejected, with a centre-screen warning. */}
      <Modal opened={rebReject != null} onClose={() => setRebReject(null)} centered size="lg"
        title={<Text fw={700} c="red.8">Rebalance rejected</Text>}>
        <Stack>
          <Alert color="red" py="xs">
            {rebReject?.label} holds more of {rebReject && rebReject.offenders.length > 1 ? 'securities that are' : 'a security that is'} not
            Approved in Securities than this portfolio does. A not-approved security can only be kept or reduced, never
            increased, so this rebalance cannot be done. Nothing on screen was changed.
          </Alert>
          <Table withTableBorder striped fz="sm">
            <Table.Thead>
              <Table.Tr><Table.Th>Not approved</Table.Th><Table.Th ta="right">Weight here</Table.Th><Table.Th ta="right">Weight in {rebReject?.label}</Table.Th></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rebReject?.offenders.map((o) => (
                <Table.Tr key={o.name}><Table.Td>{o.name}</Table.Td><Table.Td ta="right">{pctFmt(o.now)}</Table.Td><Table.Td ta="right">{pctFmt(o.want)}</Table.Td></Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Text size="sm">Approve the security in Securities, or choose another portfolio or model to rebalance to.</Text>
          <Group justify="flex-end"><Button onClick={() => setRebReject(null)}>OK</Button></Group>
        </Stack>
      </Modal>
      <Modal opened={confirmClear} onClose={() => setConfirmClear(false)} title="Clear all trades">
        <Stack>
          <Text size="sm">Clear every trade on screen, including the securities added to buy and the cash transfers? The saved draft (if any) is unchanged until you save again.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmClear(false)}>Cancel</Button>
            <Button color="red" onClick={() => { setEntries({}); setAdded([]); setTransfers([]); setDirty(true); setConfirmClear(false); }}>Clear all trades</Button>
          </Group>
        </Stack>
      </Modal>
      {/* I5 (Pedro: "a Continue / Stay (and correct) kind of option"). */}
      <Modal opened={negWarn != null} onClose={() => setNegWarn(null)} title="A cash account would go negative" size="lg">
        <Stack>
          {overdrawn.map((o) => <Text key={o.code} size="sm"><b>{o.code}</b> would end at <b>{amount(o.value)} {refCcy}</b>.</Text>)}
          {/* TP-XFER (Pedro 2026-10-24): offer the solution before saving. */}
          <Text size="sm">Fix it before saving:</Text>
          <Group gap="xs">
            <Button variant="light" onClick={() => { setNegWarn(null); setSettleOpen(true); }}>Change settlement account for some trades…</Button>
            <Tooltip withArrow disabled={cash.length >= 2} label="The portfolio has only one cash account.">
              <span><Button variant="light" disabled={cash.length < 2} onClick={() => openTransfers()}>Transfer cash from another cash account…</Button></span>
            </Tooltip>
          </Group>
          <Text size="sm">Or save the draft anyway, or stay and correct the trades yourself.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setNegWarn(null)}>Stay and correct</Button>
            <Button color="orange" onClick={() => { const asNew = !!negWarn?.asNew; setNegWarn(null); void save(asNew); }}>Continue saving</Button>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={settleOpen} onClose={() => setSettleOpen(false)} title="Change settlement account" size="lg">
        <Stack>
          <Text size="sm">The trades settling in an account that would go negative. Choose another cash account for any of them; the balances update as you go.</Text>
          {settleRows.length === 0 && <Text size="sm" c="dimmed">No trade settles in an overdrawn account any more.</Text>}
          {settleRows.length > 0 && (
            <Table withTableBorder striped fz="sm">
              <Table.Thead>
                <Table.Tr><Table.Th>Trade</Table.Th><Table.Th>Security</Table.Th><Table.Th ta="right">Value ({refCcy})</Table.Th><Table.Th>Settle in</Table.Th></Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {settleRows.map((r) => (
                  <Table.Tr key={r.key}>
                    <Table.Td>{calc[r.key]?.side === 'buy' ? 'Buy' : 'Sell'}</Table.Td>
                    <Table.Td>{r.name}</Table.Td>
                    <Table.Td ta="right">{amount(calc[r.key]?.value ?? null)}</Table.Td>
                    <Table.Td>
                      <Select size="xs" w={200} data={cashOptions} value={entries[r.key]?.settle ?? null} allowDeselect={false}
                        onChange={(v) => v && setEntry(r.key, { settle: v })} comboboxProps={{ withinPortal: true }} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
          <Text size="sm" fw={500}>Cash after the trades</Text>
          <Group gap="md">
            {cash.map((a) => {
              const v = projected(a.code, transfers);
              return <Text key={a.code} size="sm" c={v != null && v < -0.005 ? 'red' : undefined}>{cashLabel(a.code)}: <b>{amount(v)} {refCcy}</b></Text>;
            })}
          </Group>
          <Group justify="flex-end"><Button onClick={() => setSettleOpen(false)}>Done</Button></Group>
        </Stack>
      </Modal>
      <Modal opened={xferOpen} onClose={() => setXferOpen(false)} size="xl"
        title={xferScope ? `Cash transfers · ${cashLabel(xferScope)}` : 'Transfer cash between accounts'}>
        <Stack>
          <Text size="sm">
            Cash moved from {xferScope ? <b>{cashLabel(xferScope)}</b> : 'one of the portfolio\'s cash accounts'} to another. <b>Out</b> is
            what leaves, in the From account's currency; <b>In</b> is what arrives, in the To account's currency: filled at the
            custodian's rate as you type when the currencies differ (an FX trade). {xferScope
              ? 'Add a transfer for each account that receives cash. Remove them all and apply to clear the Sell tick.'
              : 'A split covering the overdraft is suggested (same currency first, then the account with the most to spare): change any amount or account.'}
            {' '}Two transfers between the same accounts in the same direction join into one when applied. Transfers are saved with the
            draft and listed under the orders.
          </Text>
          <Table withTableBorder fz="sm">
            <Table.Thead>
              <Table.Tr><Table.Th>From</Table.Th><Table.Th>To</Table.Th><Table.Th>Out</Table.Th><Table.Th>In</Table.Th><Table.Th w={40} /></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {xferRows.length === 0 && (
                <Table.Tr><Table.Td colSpan={5}><Text size="sm" c="dimmed">No transfers. Apply to save that, or add one.</Text></Table.Td></Table.Tr>
              )}
              {xferRows.map((r, i) => {
                const same = !!r.from && !!r.to && cashCcy(r.from) === cashCcy(r.to);
                // TF-XFER3: the To picker leaves out the From account itself.
                const toOptions = cashOptions.filter((o) => o.value !== r.from);
                return (
                  <Table.Tr key={i}>
                    <Table.Td>
                      {xferScope ? <Text size="sm" fw={500}>{cashLabel(r.from ?? xferScope)}</Text> : (
                        <Select size="xs" w={200} data={cashOptions} value={r.from} comboboxProps={{ withinPortal: true }}
                          onChange={(v) => editXfer(i, { from: v, ...(v && v === r.to ? { to: null } : {}) })} />
                      )}
                    </Table.Td>
                    <Table.Td><Select size="xs" w={200} data={toOptions} value={r.to} comboboxProps={{ withinPortal: true }}
                      placeholder="Receiving account" onChange={(v) => editXfer(i, { to: v })} /></Table.Td>
                    <Table.Td><TextInput size="xs" w={140} value={r.out} rightSection={<Text size="xs" c="dimmed">{r.from ? cashCcy(r.from) : ''}</Text>}
                      onChange={(e) => editXfer(i, { out: e.currentTarget.value })} /></Table.Td>
                    <Table.Td>
                      {/* TF-IN-RO: read-only, worked out from Out as it is typed. */}
                      <Tooltip withArrow multiline w={240}
                        label={same ? 'Same currency: In equals Out.' : "Out at the custodian's exchange rate of the positions' date."}>
                        <TextInput size="xs" w={140} value={r.inn} readOnly variant="filled" aria-label={`Amount in, transfer ${i + 1}`}
                          rightSection={<Text size="xs" c="dimmed">{r.to ? cashCcy(r.to) : ''}</Text>} />
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip withArrow label="Remove this transfer">
                        <ActionIcon variant="subtle" color="red" aria-label={`Remove transfer ${i + 1}`}
                          onClick={() => setXferRows((p) => p.filter((_x, j) => j !== i))}><IconTrash size={14} /></ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          <Group gap="xs">
            <Button size="xs" variant="light" onClick={() => setXferRows((p) => [...p,
              { from: xferScope, to: null, out: '', inn: '' }])}>+ Add a transfer</Button>
            <Tooltip withArrow disabled={xferRows.length > 0} label="No transfers to remove.">
              <span><Button size="xs" variant="subtle" color="red" disabled={xferRows.length === 0}
                onClick={() => setXferRows([])}>Remove all</Button></span>
            </Tooltip>
          </Group>
          <Text size="sm" fw={500}>Cash after the trades and these transfers ({refCcy})</Text>
          <Group gap="md">
            {cash.map((a) => {
              const v = projected(a.code, xferResult);
              return <Text key={a.code} size="sm" c={v != null && v < -0.005 ? 'red' : undefined}>{cashLabel(a.code)}: <b>{amount(v)} {refCcy}</b></Text>;
            })}
          </Group>
          {xferOpposite.length > 0 && <Alert color="yellow" py={4}><Stack gap={0}>{xferOpposite.map((p) => <Text key={p} size="xs">{p}</Text>)}</Stack></Alert>}
          {xferProblems.length > 0 && <Alert color="orange" py={4}><Stack gap={0}>{xferProblems.map((p) => <Text key={p} size="xs">{p}</Text>)}</Stack></Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setXferOpen(false)}>Cancel</Button>
            <Tooltip withArrow disabled={xferProblems.length === 0} label="Fix what is listed above first.">
              <span><Button onClick={applyTransfers} disabled={xferProblems.length > 0}>Apply transfers</Button></span>
            </Tooltip>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete draft">
        <Stack>
          <Text size="sm">Delete <b>{title || 'this draft'}</b>? Drafts based on it move onto its starting point and are checked again. The portfolio is unchanged.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button color="red" onClick={() => void remove()}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );

  return { enabled, active, button, panel, width, headCells, rowCells, blankCells, footRows,
    addedRows: active && single ? addedRows : [], searchCell,
    onlyTraded: active && onlyTraded, isTraded,
    // I1 (Pedro: "if it is not too hard to reverse, let's try it"): a row with a trade is shaded.
    rowShade: (r: ConsolidatedRow) => (!active || !single ? null
      : blockedKeys.has(r.key) ? 'var(--mantine-color-red-2)'   // M-REB-LOUD: a row that cannot be traded as it stands
        : isTraded(r) ? 'var(--mantine-color-orange-0)' : null) };
}
